/**
 * INSECTS — a catch-and-collect system for the garden scene. Insects appear, move about the garden for a
 * while (FLYING butterflies/bees flutter + land on flowers; CRAWLING beetles/ants path the ground; HOVERING
 * dragonflies dart without landing), and the player clicks one to collect it. This module owns the PURE data
 * + logic: the bespoke species (art specs + rarity/value + locomotion), the appear→settle→leave lifecycle
 * timings, and the caught tally. The UI (InsectLayer) owns the live sim + clicks; the render is baked SVG
 * (InsectArt) drawn as depth-sorted scene sprites.
 *
 * NOTE: the roster began as 10 butterflies; new families (beetles, bees, dragonflies, fantasy bugs) are being
 * added. Some names here still say "butterfly/wing/flap" — those apply to the FLYING family; per-species
 * `locomotion` + bespoke art/animation drive the rest.
 */

import { scoreRequireSets, type ReqCtx } from "./requireSets";
import { INSECT_HOST_SETS, withHostSets } from "./hostSets";
import { coerceReveals } from "./clueReveal";
import { coerceCondMap, coerceRarityMap, coerceTierTable, finiteAtLeast, isPlainObject, type CondSpec } from "./tuningCoerce";
import { RAIN_ANY } from "./weather";
import { isIn, isOut, type ReleaseId } from "./releases";

/** How an insect is drawn — a compact spec the procedural <InsectArt> renderer + bespoke files read. Colours
 *  are hex strings. For the FLYING (butterfly/moth) family this describes wings; crawlers/hoverers mostly read
 *  the colours + `span` and draw their own body from a bespoke file. */
export interface InsectArtSpec {
  /** forewing (upper) + hindwing (lower) outline shape — used only by the GENERIC fallback renderer. */
  foreShape: "round" | "angular" | "pointed" | "scalloped";
  hindShape: "round" | "tailed" | "lobed";
  /** primary + edge colours (a gradient from base at the body to edge at the wingtip / shell rim). */
  wing: string;
  wingEdge: string;
  /** body colour. */
  body: string;
  /** wing markings — generic-fallback only; bespoke files draw their own. */
  pattern: "none" | "spots" | "eyespots" | "bands" | "veins" | "tips" | "marble";
  patternC: string;
  /** overall size multiplier (small ant/skipper → big rhino beetle / swallowtail). */
  span: number;
  /** antenna style (flying family). */
  antenna?: "club" | "feather";
  /** a soft glow (rare/magical/luminous species — fireflies, fantasy bugs). */
  glow?: string;
  /** blur-fast wing beat (dragonflies): the scene snaps between the two baked wing EXTREMES at a high rate
   *  instead of smoothly cycling all frames, so the wings read as a rapid buzz. */
  fastWings?: boolean;
}

export interface InsectDef {
  id: string;
  name: string;
  /** the content release this belongs to (sim/releases.ts) — hidden and never granted until that release is on.
   *  Absent (everything today) = always available. */
  release?: ReleaseId;
  icon: string;              // emoji fallback (toasts/collection list)
  /** LEGACY relative spawn weight. It no longer decides anything: every one of the 70 insects now DECLARES its
   *  `rarity`, so `inferRarity(weight)` is a dead fallback kept only for a def written without one. It survives
   *  because `pickInsect()` (dev force-spawn + the preview pages) still rolls over it. Editing a weight does
   *  NOT change a species' tier or its spawn chance — edit `rarity` for the tier, `value` for the payout. */
  weight: number;
  /** RARITY TIER — sets the DEFAULT base per-attempt chance (config.tierBasePct[rarity]) and the driver ceiling
   *  (config.tierCap[rarity]). Admin-editable per insect.
   *
   *  DECLARE IT. Optional in the type only so an override/older def still compiles; all 70 shipped insects set
   *  it explicitly. Tiers used to fall out of the `weight` thresholds in inferRarity, which meant nobody had
   *  chosen them: that produced a 26-strong uncommon bulge against a 13-species common base, and stranded
   *  seven fantasy creatures in epic. The tier is now an authored decision, held to the gate-count ladder in
   *  CONDITIONS.md §6 (~2 gates at common rising to 5 at legendary) — a species' tier and the demands of its
   *  `appears` block must agree, so move both or neither. */
  rarity?: Rarity;
  /** collection value / points (future: a bug market or bounty). */
  value: number;
  /** how flighty: higher = faster, more erratic movement + shorter rests (harder to click in time). */
  skittish: number;
  /** how it MOVES + is caught. "fly" = flutter + land on flowers (butterflies/bees/moths); "crawl" = path the
   *  GROUND plane + rest on the ground/a leaf (beetles/ants — no lift, depth-sorts normally, no wing-flap);
   *  "hover" = dart + hover, NEVER lands, clickable mid-hover (dragonflies). Default "fly". */
  locomotion?: "fly" | "crawl" | "hover";
  /** ONLY TRUE BUTTERFLIES land on flower blossoms to rest. Set `lands: true` on the butterflies; every other
   *  flier (bees, moths, hoverflies, lacewings, …) flits + leaves WITHOUT landing on a bloom. Crawlers rest on
   *  the ground, hoverers never rest — neither reads this. Default false. */
  lands?: boolean;
  /** OPTIONAL per-species BEHAVIOUR — a movement personality layered on `locomotion` (see sim/insectBehavior.ts).
   *  Tunes speed/flutter/rest/restlessness; `treeCling` also targets a tree trunk. Absent ⇒ plain locomotion.
   *  `flutterer` is the ERRATIC-BUTTERFLY profile (1.8× flutter, restless re-targeting) and belongs only to
   *  actual butterflies — it was once the catch-all for everything with wings, which made a honeybee move like
   *  a drunk cabbage white. Bees fly `beeline`, weak wafting fliers `drifter`, the hoverfly `hoverHold`. */
  behavior?: import("./insectBehavior").InsectBehavior;
  // ── CATCH MINIGAME tuning (all optional; derived from rarity/skittish when absent — see catchProps) ──
  /** seconds the hold-to-track pursuit lasts before the bug auto-escapes. Absent ⇒ derived (rarer = shorter). */
  escapeTime?: number;
  /** 0..1 — how much it RESISTS Capture Chance building (0 = none, 0.5 = halves your capture rate). Absent ⇒
   *  derived from rarity. This is what makes a rare bug hard without making it impossibly fast. */
  captureResistance?: number;
  art: InsectArtSpec;
  /** OPTIONAL appear-condition. Absent ⇒ always eligible to spawn. When present, the species only spawns
   *  naturally when the garden meets EVERY clause (see AppearCond). Dev force-spawns ignore this. */
  appears?: AppearCond;
}

/**
 * A data-driven spawn condition, evaluated by the STANDARDIZED spawn-likelihood scorer (insectSpawnWeight).
 * Each field is EITHER a hard GATE (must pass or the insect can't spawn) or a soft DRIVER (more of it → higher
 * effective weight). All fields optional; an absent cond ⇒ "always eligible, base weight only".
 *
 * The scorer's model (see insectSpawnWeight): effectiveWeight = base · Π(gate ? 1 : 0) · driverScale, where
 * driverScale grows ~linearly with how much of each satisfied DRIVER is present (flowering plants, total
 * plants, purchased plot cells), softly capped. This keeps "more qualifying plants/space → more likely" a
 * single, inspectable formula rather than scattered magic numbers.
 */
export interface AppearCond {
  // ── GATES (binary; fail ⇒ weight 0) ──
  /** requires night (dusk/night — skyTone.phase ≥ 0.625). */
  night?: boolean;
  /** requires daytime (not night). */
  day?: boolean;
  // Every species' time gate is DELIBERATE — `night`, `day`, or neither with a comment on its line saying why
  // it is round-the-clock. Absence of a gate is never an oversight here, so do not "tidy" one in or out
  // without reading that comment. The round-the-clock set is: Ant, Woodlouse, Crane Fly, Green Lacewing,
  // Aphid, Garden Snail, Millipede.
  /** requires weather rain intensity ≥ this: ≤0.35 any rain, ≤0.8 heavy or storm, ≤1 storm only (the sky emits
   *  only 0 / 0.35 / 0.8 / 1). Write RAIN_ANY / RAIN_HEAVY / RAIN_STORM from weather.ts, never a bare number. */
  rainMin?: number;
  /** requires the current weather type ∈ this set (e.g. ["thunderstorm"]). */
  weather?: import("./weather").WeatherType[];
  /** hard floor: needs ≥ N FLOWERING (blossoming) plants before this insect can appear at all. */
  minFlowering?: number;
  /** hard floor: needs ≥ N plants total (a "lush / established garden" gate). */
  minPlants?: number;
  /** hard floor: needs ≥ N purchased plot cells (a "big enough garden" gate). */
  minPlots?: number;
  /** at least ONE of these flower species must be planted (a host/nectar preference gate). */
  flowers?: string[];

  // ── ADVANCED GATES (spatial / composition) ──
  /** needs ≥ `speciesMin` plants of species `speciesId` (any proximity). */
  speciesId?: string;
  speciesMin?: number;
  /** needs a CLUSTER: ≥ `clusterMin` plants of `clusterSpecies` all within `clusterRadius` garden-units of one
   *  another (a tight clump — e.g. "3 daisies in close proximity"). */
  clusterSpecies?: string;
  clusterMin?: number;
  clusterRadius?: number;
  /** needs ≥ `waterCellsMin` water cells in the garden (water-loving species). */
  waterCellsMin?: number;
  /** needs ≥ `decorMin` of decor type `decorId` placed (insects drawn to specific decor). */
  decorId?: string;
  decorMin?: number;
  /** needs plant DENSITY ≥ this (plants ÷ plot cells) — a "crowded/lush" signal. */
  densityMin?: number;

  // ── DRIVERS (soft; satisfied → effective weight scales up with the amount present) ──
  /** this insect is drawn to FLOWERS: weight scales with the count of flowering plants (nectar-seekers —
   *  butterflies, bees, hoverflies). `perFlower` = extra weight-multiplier per flowering plant. */
  likesFlowers?: number;
  /** drawn to overall LUSHNESS: weight scales with total plant count (predators, generalists). */
  likesPlants?: number;
  /** drawn to a specific host FLOWER species being present: weight scales with how many of `flowers` there are. */
  likesHostFlowers?: number;

  // ── REQUIRE-SETS (the unified attractor model) ──
  /** Zero or more attractor SETS (host plants / decor / clusters). Each set can be a GATE and/or a soft DRIVER
   *  (see RequireSet). This is the general, OR/AND/cluster-aware way to tie a creature to garden composition;
   *  the flat `flowers`/`decorId`/`clusterSpecies` fields above remain as simple shorthands. */
  sets?: import("./requireSets").RequireSet[];
}

/** The live garden context an appear-condition is scored against. Built by InsectLayer each spawn. */
export interface AppearCtx {
  species: Set<string>;      // FLOWERING species currently planted (for `flowers` host gates/drivers)
  plantCount: number;        // total plants in the garden (all forms)
  floweringCount: number;    // how many of those plants are flowering (have blossoms)
  hostCount: number;         // how many flowering plants match this insect's `flowers` list (set per-insect by the scorer)
  plotCount: number;         // purchased plot CELLS (garden space)
  night: boolean;            // is it night (phase ≥ 0.625)
  rain: number;              // current weather rain intensity 0..1
  weather: import("./weather").WeatherType;
  // ── advanced signals ──
  speciesCounts: Record<string, number>;   // count of EVERY planted species id → how many placed
  plantPos: Array<{ species: string; gx: number; gy: number }>;   // every plant's species + garden position (for clustering)
  waterCells: number;        // number of water cells in the garden
  decorCounts: Record<string, number>;     // count of each placed decor id
}

/**
 * THE 70-INSECT ROSTER. Ordered by family (butterflies → crawlers → fliers → ground bugs → hoverers), not by
 * tier; read a species' `rarity` field, not its position, for how rare it is.
 *
 * THE TIER SPREAD IS AUTHORED, AND IT IS A PYRAMID: 22 common / 15 uncommon / 17 rare / 10 epic / 6 legendary.
 * Before this pass no insect declared a `rarity` at all — the tier fell out of `inferRarity(def.weight)`, whose
 * thresholds carved the roster into 13/26/11/14/6: a bulging uncommon middle twice the size of the common base,
 * and an epic tier that had quietly become the parking space for seven fantasy creatures (Frostwing, Thornwing,
 * Stained Glass, Auroralace, Sporewing, Crystal Beetle, Geode Trapjaw). Every species now declares its tier.
 *
 * A TIER IS EARNED BY ITS CONDITION, NOT BY A NUMBER. The gate count rises monotonically with rarity —
 * 2.45 / 3.13 / 3.41 / 4.20 / 5.00 average gates per tier, counting the host set in hostSets.ts that merges in
 * as a gate — and CONDITIONS.md §6 caps EVERY creature at 5. Nothing on this roster reaches 6, and a sixth is
 * not a way to make something rarer; it is how a creature becomes unreachable without anyone noticing.
 *
 * THE LUNA MOTH STAYS LEGENDARY, and it is the one real-world animal in that tier. The rule of thumb is that
 * legendary is where the fantasy creatures live, and five of the six (Emberflutter, Voidwing, Sporeback, Prism
 * Dragonfly, Lantern Wisp) follow it. Luna is the deliberate exception: it is the single most iconic insect a
 * real garden can produce, its 180 value already sits in legendary company, and its condition is a legendary
 * SITUATION — a calm clear night over twelve plants of deep dense foliage with a night-bloomer in flower. The
 * alternative was an all-fantasy top tier, which would make the tier read as "the made-up ones" rather than
 * "the rare ones". If a future pass wants Luna demoted, that is the argument it has to beat.
 *
 * ONE KNOWN OUTLIER, recorded rather than papered over: the Prism Dragonfly wants day + lightRain, the only
 * legendary forced to share a crowded DAYTIME window (~39 species eligible, Σp≈19), so competition stretches
 * its expected time-to-see to ~4.4 h against ~45-80 min for the other five. Fixing it means either a quieter
 * window or a per-insect `basePct`, NOT a nudge to config.tierBasePct.legendary — that knob moves all six.
 */
export const INSECTS: InsectDef[] = [
  // BUTTERFLIES all `lands: true` (only butterflies perch on blossoms) and nearly all carry `likesFlowers` —
  // nectar-seeking is the family identity and the one thing they share. What they must NOT share is the reason
  // they turn up: this block previously ran 22 of 30 on `minFlowering + likesFlowers` with an optional day/night,
  // so a legendary Voidwing and a common Cabbage White were two clauses apart. Each species now owns a distinct
  // GATE — weather, a plant cluster, a named species, decor, density, water or garden size — and the gate count
  // climbs with rarity (~2 at common rising to 5 at legendary; see CONDITIONS.md sections 6 and 7).
  //
  // Two rules a future editor must not casually break:
  //  - Every butterfly ALSO has a host set in hostSets.ts that merges in automatically (effectiveAppears) and
  //    counts as a gate. Read a condition here together with that set, or you will double-gate a species.
  //  - Hard `speciesId`/`clusterSpecies` gates name only plants LEVELING can unlock. Never gate on a PREMIUM
  //    (real-money) plant — voidOrchid, nightglowMushroom, lightbloom, bloodshadowRose, abyssBlossom,
  //    vampiricPitcher, auroraFern, waterDandelion, lilyOfTheDead — or the species becomes unobtainable.
  //    This binds the merged HOST SETS too, and that is where it had actually broken: a `use: "both"` set is a
  //    hard gate, so an all-premium set (Voidwing's "void garden") or an `all` recipe with one premium item
  //    (Auroralace's "aurora glade") paywalled the species and the 70-species `full_cabinet` achievement behind it.
  //    Both now carry a free path; hostSets.ts owns the rule and scripts/smoke.mjs asserts it.
  {
    id: "cabbageWhite", name: "Cabbage White", icon: "🦋", weight: 40, rarity: "common", value: 5, skittish: 0.3, lands: true, behavior: "flutterer",
    art: { foreShape: "round", hindShape: "round", wing: "#f4f6f8", wingEdge: "#dfe6ec", body: "#3a3a3a",
      pattern: "spots", patternC: "#4a4a4a", span: 0.9, antenna: "club" },
    appears: { day: true, minFlowering: 1, likesFlowers: 0.35 },   // the baseline: one bloom, any bloom, in daylight. Deliberately the roster's only unconditional nectar-seeker — `day` is species biology (butterflies do not fly at night), not a difficulty clause.
  },
  {
    id: "sulphur", name: "Clouded Sulphur", icon: "🦋", weight: 32, rarity: "common", value: 8, skittish: 0.36, lands: true, behavior: "flutterer",
    art: { foreShape: "round", hindShape: "round", wing: "#ffe14a", wingEdge: "#f4b52e", body: "#5a4a2a",
      pattern: "tips", patternC: "#8a6a2a", span: 0.92, antenna: "club" },
    appears: { day: true, weather: ["clear"], likesFlowers: 0.32 },   // a sun-basker: daylight AND an OPEN, cloudless sky — the roster's cheapest weather gate, so a new garden still meets it
  },
  {
    id: "orangeTip", name: "Orange Tip", icon: "🦋", weight: 26, rarity: "common", value: 12, skittish: 0.42, lands: true, behavior: "flutterer",
    art: { foreShape: "pointed", hindShape: "round", wing: "#fbfcfd", wingEdge: "#eef2f5", body: "#3a3a3a",
      pattern: "tips", patternC: "#ff7a2e", span: 0.9, antenna: "club" },
    appears: { day: true, speciesId: "pansy", speciesMin: 2, likesFlowers: 0.3 },   // a spring crucifer-feeder: wants a PAIR of pansies, not one bloom. Pansy is an early unlock, so this stays a common-tier ask.
  },
  {
    id: "commonBlue", name: "Common Blue", icon: "🦋", weight: 24, rarity: "uncommon", value: 15, skittish: 0.5, lands: true, behavior: "flutterer",
    art: { foreShape: "round", hindShape: "round", wing: "#5a8fe0", wingEdge: "#3f6ec4", body: "#2a2a3a",
      pattern: "bands", patternC: "#eaeef6", span: 0.78, antenna: "club" },
    appears: { day: true, clusterSpecies: "daisy", clusterMin: 3, clusterRadius: 2.5, likesFlowers: 0.3 },   // a meadow butterfly — needs a real daisy patch, not a scattered pair
  },
  {
    id: "painted", name: "Painted Lady", icon: "🦋", weight: 20, rarity: "uncommon", value: 20, skittish: 0.52, lands: true, behavior: "flutterer",
    art: { foreShape: "angular", hindShape: "round", wing: "#e8863c", wingEdge: "#c85a2a", body: "#3a2a1a",
      pattern: "marble", patternC: "#2a1a12", span: 1.0, antenna: "club" },
    appears: { day: true, densityMin: 0.5, likesFlowers: 0.28 },   // the migrant: arrives at a CROWDED border — density rather than raw count, so a small packed garden qualifies
  },
  {
    id: "peacock", name: "Peacock", icon: "🦋", weight: 15, rarity: "uncommon", value: 30, skittish: 0.58, lands: true, behavior: "basker",
    art: { foreShape: "scalloped", hindShape: "lobed", wing: "#a83030", wingEdge: "#6a1c1c", body: "#2a1414",
      pattern: "eyespots", patternC: "#3a6fb0", span: 1.05, antenna: "club" },
    appears: { day: true, decorId: "log", decorMin: 1, likesFlowers: 0.26 },   // overwinters in dead wood — a fallen LOG is the tell, and its showy-bloom host set supplies the nectar half
  },
  {
    id: "monarch", name: "Monarch", icon: "🦋", weight: 12, rarity: "rare", value: 40, skittish: 0.55, lands: true, behavior: "basker",
    art: { foreShape: "angular", hindShape: "round", wing: "#f08a2a", wingEdge: "#c85a1a", body: "#1a1a1a",
      pattern: "veins", patternC: "#161616", span: 1.1, antenna: "club" },
    // prefers nectar-rich hosts; scales hard with how many of them are present
    appears: { day: true, clusterSpecies: "zinnia", clusterMin: 3, clusterRadius: 3, likesHostFlowers: 0.5, likesFlowers: 0.12 },   // the migrant needs a real fuelling station: a zinnia STAND, not a lone bloom. Its aster/zinnia host set supplies the nectar driver.
  },
  {
    id: "morpho", name: "Blue Morpho", icon: "🦋", weight: 8, rarity: "rare", value: 65, skittish: 0.64, lands: true, behavior: "basker",
    art: { foreShape: "round", hindShape: "lobed", wing: "#3aa0ff", wingEdge: "#1f5fd0", body: "#20242e",
      pattern: "bands", patternC: "#0e2a55", span: 1.18, antenna: "club" },
    appears: { day: true, weather: ["clear"], densityMin: 0.9, likesFlowers: 0.18 },   // a rainforest exotic: a dense canopy under an open tropical sky. Density, not raw count — a crowded small garden reads better than a sparse big one.
  },
  {
    id: "swallowtail", name: "Tiger Swallowtail", icon: "🦋", weight: 6, rarity: "epic", value: 95, skittish: 0.7, lands: true, behavior: "basker",
    art: { foreShape: "pointed", hindShape: "tailed", wing: "#ffd23a", wingEdge: "#e0a022", body: "#1a1a1a",
      pattern: "veins", patternC: "#141414", span: 1.28, antenna: "club" },
    appears: { day: true, speciesId: "hibiscus", speciesMin: 2, minFlowering: 5, likesHostFlowers: 0.55 },   // a big swallowtail needs big blooms in QUANTITY: two hibiscus, not one of anything
  },
  {
    // LUNA is a MOTH, not a butterfly → NO `lands` (it won't perch on a bloom). Nocturnal.
    id: "luna", name: "Luna Moth", icon: "🌙", weight: 3, rarity: "legendary", value: 180, skittish: 0.6, behavior: "basker",
    art: { foreShape: "round", hindShape: "tailed", wing: "#9fe6b0", wingEdge: "#6fc98a", body: "#e8e2d0",
      pattern: "eyespots", patternC: "#c98a5a", span: 1.35, antenna: "feather", glow: "#c8f0d8" },
    appears: { night: true, weather: ["clear", "partlyCloudy"], minPlants: 12, densityMin: 0.8, likesPlants: 0.1 },   // a silkmoth with no mouth: it never feeds, so this is the one butterfly-slot creature with NO flower clause — deep still foliage on a calm night
  },

  // ════════════ BUTTERFLY WAVE 3 — 10 REAL + 10 FANTASY (all `lands: true`, all flower-attracted) ════════════
  {
    id: "redAdmiral", name: "Red Admiral", icon: "🦋", weight: 22, rarity: "uncommon", value: 18, skittish: 0.5, lands: true, behavior: "basker",
    art: { foreShape: "angular", hindShape: "round", wing: "#1c1c1c", wingEdge: "#0e0e0e", body: "#161616",
      pattern: "bands", patternC: "#e23b2e", span: 1.0, antenna: "club" },
    appears: { day: true, weather: ["partlyCloudy", "mostlyCloudy", "overcast"], minPlants: 6, likesFlowers: 0.28 },   // flies on DULL days when the sun-lovers sit them out — the overcast counterpart to the Gulf Fritillary's open sky. Dull DAYS, not dusk: `day` is what keeps the grey-weather butterfly out of the night.
  },
  {
    id: "gulfFritillary", name: "Gulf Fritillary", icon: "🦋", weight: 20, rarity: "uncommon", value: 22, skittish: 0.52, lands: true, behavior: "basker",
    art: { foreShape: "pointed", hindShape: "round", wing: "#e8862a", wingEdge: "#c8641a", body: "#3a2416",
      pattern: "spots", patternC: "#1a1a1a", span: 1.02, antenna: "club" },
    appears: { day: true, weather: ["clear"], minFlowering: 3, likesFlowers: 0.28 },   // a subtropical: daylight under a settled cloudless sky
  },
  {
    id: "smallCopper", name: "Small Copper", icon: "🦋", weight: 20, rarity: "uncommon", value: 20, skittish: 0.5, lands: true, behavior: "flutterer",
    art: { foreShape: "round", hindShape: "round", wing: "#e8752a", wingEdge: "#b8541a", body: "#3a2416",
      pattern: "spots", patternC: "#3a2416", span: 0.72, antenna: "club" },
    appears: { day: true, decorId: "pebbles", decorMin: 1, likesFlowers: 0.3 },   // a territorial basker — perches on warm bare stone
  },
  {
    id: "hollyBlue", name: "Holly Blue", icon: "🦋", weight: 22, rarity: "uncommon", value: 18, skittish: 0.52, lands: true, behavior: "flutterer",
    art: { foreShape: "round", hindShape: "round", wing: "#9ab8ec", wingEdge: "#6f92cc", body: "#2a3040",
      pattern: "none", patternC: "#2a3a5a", span: 0.72, antenna: "club" },
    appears: { day: true, speciesId: "englishIvy", speciesMin: 2, likesFlowers: 0.28 },   // the second brood breeds on ivy; the holly half comes from its host set
  },
  {
    id: "speckledWood", name: "Speckled Wood", icon: "🦋", weight: 18, rarity: "uncommon", value: 22, skittish: 0.48, lands: true, behavior: "flutterer",
    art: { foreShape: "round", hindShape: "round", wing: "#5a4832", wingEdge: "#3a2e1e", body: "#241c12",
      pattern: "spots", patternC: "#d8c89a", span: 0.9, antenna: "club" },
    appears: { day: true, minPlants: 8, densityMin: 0.7, likesPlants: 0.12, likesFlowers: 0.1 },   // a woodland shade species: wants leaf cover by day, and is the one butterfly that prefers foliage to flowers. SHADE is not darkness — it still flies only in daylight.
  },
  {
    id: "comma", name: "Comma", icon: "🦋", weight: 16, rarity: "rare", value: 26, skittish: 0.54, lands: true, behavior: "flutterer",
    art: { foreShape: "scalloped", hindShape: "lobed", wing: "#d8742a", wingEdge: "#a8501a", body: "#3a2416",
      pattern: "marble", patternC: "#3a2416", span: 0.94, antenna: "club" },
    appears: { day: true, weather: ["fog", "overcast"], densityMin: 0.6, likesFlowers: 0.24 },   // a ragged leaf-mimic: soft damp DAYLIGHT and enough LEAF COVER to disappear into
  },
  {
    id: "marbledWhite", name: "Marbled White", icon: "🦋", weight: 15, rarity: "rare", value: 30, skittish: 0.54, lands: true, behavior: "flutterer",
    art: { foreShape: "round", hindShape: "lobed", wing: "#f2f2ee", wingEdge: "#d8d8d0", body: "#1c1c1c",
      pattern: "marble", patternC: "#1c1c1c", span: 0.98, antenna: "club" },
    appears: { day: true, speciesId: "blueFescue", speciesMin: 3, minPlots: 16, likesFlowers: 0.22 },   // a true grassland species: lays on GRASS, and wants open meadow to patrol rather than a tight clump
  },
  {
    id: "viceroy", name: "Viceroy", icon: "🦋", weight: 12, rarity: "rare", value: 40, skittish: 0.56, lands: true, behavior: "basker",
    art: { foreShape: "angular", hindShape: "round", wing: "#e88a2a", wingEdge: "#c05a1a", body: "#161616",
      pattern: "veins", patternC: "#161616", span: 1.06, antenna: "club" },
    appears: { day: true, waterCellsMin: 20, likesFlowers: 0.22 },   // the Monarch's mimic: same daylight, but a wet-margin willow-feeder rather than a zinnia patch — water is what separates the two
  },
  {
    id: "zebraLongwing", name: "Zebra Longwing", icon: "🦋", weight: 9, rarity: "rare", value: 55, skittish: 0.58, lands: true, behavior: "basker",
    art: { foreShape: "pointed", hindShape: "round", wing: "#1c1c1c", wingEdge: "#0e0e0e", body: "#161616",
      pattern: "bands", patternC: "#f2d23a", span: 1.14, antenna: "club" },
    appears: { day: true, minPlots: 20, likesFlowers: 0.2 },   // a long-lived pollen-feeder that traplines a fixed route: it needs a big garden to patrol, not just a rich one
  },
  {
    id: "mourningCloak", name: "Mourning Cloak", icon: "🦋", weight: 7, rarity: "epic", value: 75, skittish: 0.6, lands: true, behavior: "basker",
    art: { foreShape: "scalloped", hindShape: "lobed", wing: "#4a1e28", wingEdge: "#2a1016", body: "#1a1014",
      pattern: "bands", patternC: "#3a6fb0", span: 1.16, antenna: "club" },
    appears: { day: true, weather: ["fog", "overcast"], decorId: "stump", decorMin: 1, minPlants: 8, likesPlants: 0.14, likesFlowers: 0.1 },   // a sap-feeder, not a nectar specialist: bare wood and cold grey daytime air
  },
  // ── FANTASY butterflies (glow) ──
  {
    id: "tidewing", name: "Tidewing", icon: "🦋", weight: 16, rarity: "uncommon", value: 34, skittish: 0.52, lands: true, behavior: "flutterer",
    art: { foreShape: "round", hindShape: "lobed", wing: "#5ec8d8", wingEdge: "#3a9aae", body: "#1a3038",
      pattern: "none", patternC: "#1a3038", span: 0.96, antenna: "club", glow: "#a8f0ff" },
    appears: { day: true, waterCellsMin: 30, likesFlowers: 0.22 },   // the waterside one: the only butterfly that needs real WATER, which is what makes its reed-cluster host set mean anything
  },
  {
    id: "prismwing", name: "Prismwing", icon: "🌈", weight: 12, rarity: "rare", value: 60, skittish: 0.58, lands: true, behavior: "flutterer",
    art: { foreShape: "pointed", hindShape: "round", wing: "#e8e0f4", wingEdge: "#cfc4e2", body: "#2a2438",
      pattern: "none", patternC: "#2a2438", span: 1.02, antenna: "club", glow: "#e8d6ff" },
    appears: { day: true, weather: ["lightRain"], minFlowering: 4, likesFlowers: 0.2 },   // refraction needs daylight AND rain at once — the rainbow condition, and the only one that wants a shower it can still fly in. The bloom floor is PROGRESSION, not flavour: without it a rare sat at the same garden-effort as a Cabbage White.
  },
  {
    id: "lumenwing", name: "Lumenwing", icon: "🏮", weight: 10, rarity: "rare", value: 72, skittish: 0.56, lands: true, behavior: "basker",
    art: { foreShape: "round", hindShape: "round", wing: "#f2c24a", wingEdge: "#c8942a", body: "#2a2012",
      pattern: "none", patternC: "#2a2012", span: 1.0, antenna: "club", glow: "#ffcf6a" },
    appears: { night: true, decorId: "lantern", decorMin: 1, minPlots: 10, likesFlowers: 0.2 },   // drawn to lantern-light after dark — the only creature that wants a piece of DECOR lit; its glow-flora host set is the other half. The plot floor is what makes a lit garden rather than a lit corner, and keeps a rare off the effort-9 shelf.
  },
  {
    id: "frostwing", name: "Frostwing", icon: "❄️", weight: 8, rarity: "epic", value: 90, skittish: 0.6, lands: true, behavior: "flutterer",
    art: { foreShape: "round", hindShape: "tailed", wing: "#cfe8f4", wingEdge: "#a8cee0", body: "#2a3a44",
      pattern: "none", patternC: "#cdefff", span: 1.08, antenna: "club", glow: "#cdefff" },
    appears: { day: true, weather: ["fog"], speciesId: "frostfern", speciesMin: 2, minFlowering: 3, likesFlowers: 0.18 },   // only in freezing mist, over a stand of frostfern
  },
  {
    id: "thornwing", name: "Thornwing", icon: "🌿", weight: 8, rarity: "epic", value: 95, skittish: 0.56, lands: true, behavior: "basker",
    art: { foreShape: "scalloped", hindShape: "lobed", wing: "#35786e", wingEdge: "#1d4a48", body: "#14201f",
      pattern: "veins", patternC: "#23514d", span: 1.06, antenna: "club", glow: "#7ad6bc" },
    appears: { night: true, flowers: ["rose", "peony", "hibiscus"], clusterSpecies: "rose", clusterMin: 3, clusterRadius: 2.5, likesHostFlowers: 0.45 },   // a thicket-dweller: a dense rose bramble after dark
  },
  {
    id: "stainedglass", name: "Stained Glass", icon: "🪟", weight: 6, rarity: "epic", value: 120, skittish: 0.62, lands: true, behavior: "basker",
    art: { foreShape: "angular", hindShape: "lobed", wing: "#2a2a3a", wingEdge: "#161624", body: "#12121a",
      pattern: "none", patternC: "#1a1a1a", span: 1.1, antenna: "club", glow: "#c8b8ff" },
    appears: { day: true, weather: ["clear"], speciesId: "gladiolus", speciesMin: 2, likesFlowers: 0.16 },   // wings lit from behind: open sun through tall jewel-toned spikes
  },
  {
    id: "auroralace", name: "Auroralace", icon: "🌌", weight: 5, rarity: "epic", value: 150, skittish: 0.6, lands: true, behavior: "flutterer",
    art: { foreShape: "round", hindShape: "lobed", wing: "#1e2450", wingEdge: "#0d1030", body: "#161a34",
      pattern: "none", patternC: "#6ff0c4", span: 1.12, antenna: "club", glow: "#8affce" },
    appears: { night: true, weather: ["clear"], minPlots: 24, likesFlowers: 0.18 },   // an aurora needs a CLEAR night sky and an open garden to see it from — the only creature gated on unobstructed space
  },
  {
    id: "sporewing", name: "Sporewing", icon: "🍄", weight: 5, rarity: "epic", value: 160, skittish: 0.5, lands: true, behavior: "flutterer",
    art: { foreShape: "round", hindShape: "lobed", wing: "#5a4a6a", wingEdge: "#3a2e48", body: "#241c30",
      pattern: "none", patternC: "#c89aff", span: 1.04, antenna: "feather", glow: "#c89aff" },
    appears: { night: true, rainMin: RAIN_ANY, densityMin: 0.8, likesFlowers: 0.14, likesPlants: 0.1 },   // wet, crowded and dark — the rot-floor condition. Keeps its rain gate; the spore host set does the rest.
  },
  {
    id: "emberflutter", name: "Emberflutter", icon: "🔥", weight: 4, rarity: "legendary", value: 200, skittish: 0.64, lands: true, behavior: "flutterer",
    art: { foreShape: "pointed", hindShape: "lobed", wing: "#2a1c18", wingEdge: "#181010", body: "#1a1210",
      pattern: "none", patternC: "#ff7a2a", span: 1.14, antenna: "feather", glow: "#ff7a2a" },
    appears: { night: true, weather: ["clear"], speciesId: "emberbloom", speciesMin: 3, minFlowering: 8, likesFlowers: 0.14 },   // drought-heat lingering after dark over a bed of emberbloom — night with a cloudless, rainless sky is the rare half. A BED, not a pair: 3 emberbloom in a garden already 8 blooms deep. Still 5 gates — the numbers moved, not the clause count.
  },
  {
    id: "voidwing", name: "Voidwing", icon: "🔮", weight: 3, rarity: "legendary", value: 300, skittish: 0.68, lands: true, behavior: "basker",
    art: { foreShape: "angular", hindShape: "round", wing: "#1a1430", wingEdge: "#0e0a1c", body: "#141024",
      pattern: "none", patternC: "#c6a0ff", span: 1.2, antenna: "club", glow: "#b48aff" },
    appears: { night: true, weather: ["thunderstorm"], minFlowering: 6, minPlots: 30, likesFlowers: 0.14 },   // the rarest situation in the game: a storm at night over a large, heavily planted garden. Its void-garden host set adds the composition half.
  },

  // ──────────────────────────── CRAWLERS (walk the ground plane) ────────────────────────────
  //
  // GARDEN BUGS (crawlers through the fantasy hoverers) are predators, grazers and generalists — NOT
  // nectar-feeders. Only the Bumblebee and the Rose Chafer are gated on blooms at all; everything else reads
  // the SHAPE of the garden instead. This block previously ran 7 species on `minPlants + likesPlants` and 8
  // more on `day` + the same, which made a Praying Mantis, a Jewel Scarab and a Green Shield Bug identical to
  // the spawn scorer. The differentiating axes are now:
  //   • densityMin  — cover-seekers (Mantis ambushes at 0.9, the densest gate here; Shield Bug, Firefly)
  //   • minPlots    — species that need ROOM to range (Grasshopper, Jewel Scarab, and both legendary hoverers)
  //   • waterCellsMin — the water-BREEDERS. Dragonfly and Prism Dragonfly lay in the pond; they were gated on
  //     plot count, which any garden has. Values ladder 25 → 40 with rarity; the whole garden's water counts,
  //     so these sit well under the fish `pondAreaMin` scale (90–240 for one pond) and stay reachable.
  //   • decorId     — shelter-under species (Woodlouse under a log, Sporeback on a stump)
  //   • weather     — Cricket (fair-weather chirping) and Rhino/Jewel/Crystal (their shells need hard light)
  //
  // The three legendaries were the worst offenders: Sporeback was `night + rainMin`, Prism Dragonfly was a
  // plant count, and Lantern Wisp was `night` ALONE — one clause for the rarest bug in the block. All three
  // now sit at the tier's 5-gate target. See CONDITIONS.md sections 6 and 7; note that every species here also
  // carries a host set in hostSets.ts that merges in automatically and COUNTS AS A GATE.
  {
    id: "ladybug", name: "Seven-spot Ladybug", icon: "🐞", weight: 38, rarity: "common", value: 6, skittish: 0.2, locomotion: "crawl", behavior: "groundBusy",
    // crawler: the art reads spec.wing (shell red) / wingEdge (rim) / body (black head+spots+legs); shape
    // levers unused (bespoke). span sizes the beetle.
    art: { foreShape: "round", hindShape: "round", wing: "#d8342a", wingEdge: "#9c1e18", body: "#1a1a1a",
      pattern: "spots", patternC: "#1a1a1a", span: 0.72 },
    appears: { day: true, likesPlants: 0.14 },   // an aphid-hunter that quarters the garden by day; its aphid-prone-tender-growth host set is what actually places it
  },
  {
    id: "ant", name: "Garden Ant", icon: "🐜", weight: 36, rarity: "common", value: 6, skittish: 0.28, locomotion: "crawl", behavior: "groundBusy",
    art: { foreShape: "round", hindShape: "round", wing: "#3a2a22", wingEdge: "#2a1c16", body: "#241812",
      pattern: "none", patternC: "#241812", span: 0.5 },
    appears: { minPlots: 1, likesPlants: 0.06 },   // forages any established plot — the roster's cheapest gate, and the only bug that asks for GROUND rather than greenery. NO TIME GATE ON PURPOSE: a colony forages in shifts around the clock.
  },
  {
    id: "woodlouse", name: "Woodlouse", icon: "🪲", weight: 30, rarity: "common", value: 8, skittish: 0.12, locomotion: "crawl", behavior: "trundle",
    art: { foreShape: "round", hindShape: "round", wing: "#7a828c", wingEdge: "#4e555e", body: "#3a4048",
      pattern: "bands", patternC: "#4e555e", span: 0.66 },
    appears: { decorId: "log", decorMin: 1 },   // it lives UNDER things — rotting wood is the whole requirement, and its damp-leaf-litter host set supplies the rest. NO TIME GATE ON PURPOSE: it is night-ACTIVE but never leaves the damp dark under the log, so turning it up by day reads correctly.
  },
  {
    id: "shieldBug", name: "Green Shield Bug", icon: "🛡️", weight: 28, rarity: "common", value: 10, skittish: 0.24, locomotion: "crawl", behavior: "trundle",
    art: { foreShape: "round", hindShape: "round", wing: "#6fae3e", wingEdge: "#3f6f22", body: "#3f6f22",
      pattern: "spots", patternC: "#2e5018", span: 0.66 },
    appears: { day: true, densityMin: 0.5, likesPlants: 0.1 },   // a sap-feeder that wants LEAF CONTACT: crowding, not a raw plant count, is what puts foliage within reach. Day-active, like the ladybug that hunts it.
  },
  {
    id: "leafBeetle", name: "Emerald Leaf Beetle", icon: "🪲", weight: 26, rarity: "common", value: 12, skittish: 0.26, locomotion: "crawl", behavior: "skitter",
    art: { foreShape: "round", hindShape: "round", wing: "#2fae6a", wingEdge: "#1a7a48", body: "#141a16",
      pattern: "none", patternC: "#141a16", span: 0.58 },
    appears: { day: true, minPlants: 2, likesPlants: 0.12 },   // grazes leaves by day — deliberately the plainest of the crawlers, so its brassica host set is the whole story
  },

  // ─────────────────────────── UNCOMMON (first fliers appear) ───────────────────────────
  {
    id: "bumblebee", name: "Bumblebee", icon: "🐝", weight: 24, rarity: "common", value: 14, skittish: 0.44, locomotion: "fly", behavior: "beeline",
    art: { foreShape: "round", hindShape: "round", wing: "#f2b81e", wingEdge: "#c88e10", body: "#1a1712",
      pattern: "bands", patternC: "#1a1712", span: 0.7 },
    appears: { day: true, minFlowering: 1, likesFlowers: 0.3 },   // the block's one true nectar-forager (does NOT land — no `lands`); the only garden bug gated on blooms at all
  },
  {
    id: "grasshopper", name: "Grasshopper", icon: "🦗", weight: 22, rarity: "common", value: 16, skittish: 0.4, locomotion: "crawl", behavior: "hopSkip",
    art: { foreShape: "round", hindShape: "round", wing: "#7aad3e", wingEdge: "#4f7a24", body: "#243018",
      pattern: "none", patternC: "#243018", span: 0.72 },
    appears: { day: true, minPlots: 12, likesPlants: 0.1 },   // a field species: it wants OPEN GROUND to leap across, not a dense thicket — the inverse of the Shield Bug
  },
  {
    id: "firefly", name: "Firefly", icon: "✨", weight: 20, rarity: "uncommon", value: 20, skittish: 0.46, locomotion: "fly", behavior: "drifter",
    art: { foreShape: "round", hindShape: "round", wing: "#2a2622", wingEdge: "#1a1714", body: "#2a2622",
      pattern: "none", patternC: "#c8402a", span: 0.6, glow: "#c8ff6a" },
    appears: { night: true, densityMin: 0.6, likesPlants: 0.08 },   // glows after dark over long damp grass — needs cover to rise out of, not bare plots
  },
  {
    id: "roseChafer", name: "Rose Chafer", icon: "🪲", weight: 18, rarity: "uncommon", value: 22, skittish: 0.3, locomotion: "crawl", behavior: "basker",
    art: { foreShape: "round", hindShape: "round", wing: "#33a05a", wingEdge: "#1f6e3c", body: "#101610",
      pattern: "none", patternC: "#101610", span: 0.68 },
    appears: { day: true, flowers: ["rose", "peony"], likesHostFlowers: 0.5 },   // lives on roses — a daylight basker that eats the petals it sits on
  },
  {
    id: "cricket", name: "Field Cricket", icon: "🦗", weight: 16, rarity: "common", value: 24, skittish: 0.42, locomotion: "crawl", behavior: "hopSkip",
    art: { foreShape: "round", hindShape: "round", wing: "#4a3522", wingEdge: "#2c2016", body: "#160f0a",
      pattern: "none", patternC: "#160f0a", span: 0.64 },
    appears: { night: true, weather: ["clear", "partlyCloudy"], likesPlants: 0.08 },   // chirps on warm dry nights — crickets fall silent in rain, so this is the fair-weather counterpart to the Woodlouse
  },

  // ─────────────────────────── RARE (showpieces + the hoverer) ───────────────────────────
  {
    id: "dragonfly", name: "Dragonfly", icon: "🪰", weight: 14, rarity: "rare", value: 30, skittish: 0.62, locomotion: "hover", behavior: "hoverDash",   // a hawker: it hangs on a point over the pond, then bolts straight to the next one
    art: { foreShape: "round", hindShape: "round", wing: "#22b0a0", wingEdge: "#137a86", body: "#20303a",
      pattern: "none", patternC: "#20303a", span: 0.9, fastWings: true },
    appears: { day: true, waterCellsMin: 25, likesPlants: 0.06 },   // a WATER-BREEDING hunter: its nymphs live in the pond, so real water — not plot count — is the requirement. Its pond-margin reed cluster is the other half.
  },
  {
    id: "stagBeetle", name: "Stag Beetle", icon: "🪲", weight: 12, rarity: "rare", value: 40, skittish: 0.34, locomotion: "crawl", behavior: "treeCling",
    art: { foreShape: "round", hindShape: "round", wing: "#4a2c1e", wingEdge: "#2c1810", body: "#160d08",
      pattern: "none", patternC: "#160d08", span: 0.9 },
    appears: { night: true, minPlants: 8, likesPlants: 0.08 },   // males fly at dusk to find rotting timber; its host set already demands a TREE to climb, which is what makes this one specific. 8 plants, not 4: a tree big enough to breed in belongs to an established garden.
  },
  {
    id: "mantis", name: "Praying Mantis", icon: "🦗", weight: 10, rarity: "rare", value: 48, skittish: 0.3, locomotion: "crawl", behavior: "ambush",
    art: { foreShape: "round", hindShape: "round", wing: "#6fb04a", wingEdge: "#47792c", body: "#1a2410",
      pattern: "none", patternC: "#1a2410", span: 0.92 },
    appears: { day: true, densityMin: 0.9, likesPlants: 0.07 },   // an ambush predator needs somewhere to be INVISIBLE: the densest-cover gate in the block, and what separates it from the Stag Beetle and the Scarab
  },
  {
    id: "rhinoBeetle", name: "Rhinoceros Beetle", icon: "🪲", weight: 8, rarity: "rare", value: 65, skittish: 0.36, locomotion: "crawl", behavior: "treeCling",
    art: { foreShape: "round", hindShape: "round", wing: "#5a3e26", wingEdge: "#39271a", body: "#1e140c",
      pattern: "none", patternC: "#1e140c", span: 1.05 },
    appears: { night: true, weather: ["clear"], minPlants: 10, likesPlants: 0.06 },   // a nocturnal giant of hot dry places — its arid succulent-bed host set means nothing in the rain. The plant floor is the biggest beetle in the garden asking for a garden worth the name: it was the only rare making NO scale ask.
  },
  {
    id: "jewelScarab", name: "Emerald Jewel Scarab", icon: "💎", weight: 6, rarity: "epic", value: 95, skittish: 0.4, locomotion: "crawl", behavior: "basker",
    art: { foreShape: "round", hindShape: "round", wing: "#28c268", wingEdge: "#149a4e", body: "#0e2416",
      pattern: "none", patternC: "#0e2416", span: 0.78 },
    appears: { day: true, weather: ["clear"], minPlots: 16, likesPlants: 0.05 },   // its shell only fires in open sun over bare desert ground — the cloudless sky is the point, not mere daylight
  },

  // ─────────────────────────── LEGENDARY / FANTASY (the "wow" finds) ───────────────────────────
  {
    id: "emberwing", name: "Emberwing Moth", icon: "🔥", weight: 7, rarity: "epic", value: 70, skittish: 0.58, locomotion: "fly", behavior: "basker",
    art: { foreShape: "round", hindShape: "round", wing: "#3a3230", wingEdge: "#221c1a", body: "#1a1512",
      pattern: "veins", patternC: "#ff6a22", span: 1.15, antenna: "feather", glow: "#ff7a2a" },
    appears: { night: true, weather: ["clear"], minPlants: 12, likesPlants: 0.05 },   // its embers only show against a dark, cloudless sky; the ember-pair host set supplies the fuel. 12 plants, not 6 — at 6 this epic became eligible in the same garden as the commons.
  },
  {
    id: "crystalBeetle", name: "Crystal Beetle", icon: "🔮", weight: 5, rarity: "epic", value: 120, skittish: 0.34, locomotion: "crawl", behavior: "trundle",
    art: { foreShape: "round", hindShape: "round", wing: "#9fd8f0", wingEdge: "#6aa8d0", body: "#243040",
      pattern: "none", patternC: "#243040", span: 0.72, glow: "#bfeaff" },
    appears: { day: true, weather: ["clear"], densityMin: 0.7, likesPlants: 0.04 },   // facets need an unclouded light source and a grown garden to scatter it through
  },
  {
    id: "sporeback", name: "Sporeback Beetle", icon: "🍄", weight: 4, rarity: "legendary", value: 150, skittish: 0.14, locomotion: "crawl", behavior: "trundle",
    art: { foreShape: "round", hindShape: "round", wing: "#5a7a4a", wingEdge: "#3a5230", body: "#1a2416",
      pattern: "none", patternC: "#1a2416", span: 0.78, glow: "#8affc0" },
    appears: { night: true, rainMin: RAIN_ANY, decorId: "stump", decorMin: 1, minPlants: 16, likesPlants: 0.04 },   // a fungal glow-bug: a wet dark night, dead wood to fruit on, and a garden deep enough to rot in. Was night+rain alone — far too cheap for a legendary. The old cover clause was `densityMin: 0.8`, which IS A RATIO and says nothing about size — it was satisfiable in a 5-plant garden, leaving this the earliest legendary on the roster. `minPlants: 16` replaces it rather than joining it: with the stump set that would have been a SIXTH gate, and §6 caps every creature at five.
  },
  {
    id: "prismDragonfly", name: "Prism Dragonfly", icon: "🌈", weight: 3, rarity: "legendary", value: 200, skittish: 0.7, locomotion: "hover", behavior: "hoverDash",   // the same long-bodied hawker frame as the Dragonfly, so the same hang-then-bolt
    art: { foreShape: "round", hindShape: "round", wing: "#7ad0d8", wingEdge: "#4a9aa8", body: "#2a2438",
      pattern: "none", patternC: "#2a2438", span: 0.95, glow: "#c8b8ff", fastWings: true },
    appears: { day: true, weather: ["lightRain"], waterCellsMin: 40, minPlots: 20, likesPlants: 0.04 },   // sunlight split through falling rain over open water — the rainbow condition, and like the common Dragonfly it BREEDS in the pond rather than merely flying over the plot
  },
  {
    id: "lanternWisp", name: "Lantern Wisp", icon: "🏮", weight: 2, rarity: "legendary", value: 260, skittish: 0.6, locomotion: "hover", behavior: "hoverHold",
    art: { foreShape: "round", hindShape: "round", wing: "#2a2418", wingEdge: "#1a160e", body: "#2a2418",
      pattern: "none", patternC: "#fff2c0", span: 0.85, glow: "#ffcf6a" },
    appears: { night: true, weather: ["fog"], waterCellsMin: 30, minPlots: 24, likesPlants: 0.03 },   // the marsh-light itself: fog over still water on a dark night, in a garden big enough to get lost in. Was `night` alone — the cheapest legendary on the roster.
  },

  // ════════════════════ SECOND WAVE — 20 real garden species (unconditional for now) ════════════════════
  // ── FLIERS ──
  // POLLINATORS & FLIERS. Small group, so each hook is a real-world one and each signature is UNIQUE across
  // all 70 insects (checked, not assumed):
  //   • Honeybee vs Hoverfly were byte-identical (`day + minFlowering`). They are now deliberate inverses: the
  //     true bee is a colony forager that recruits nestmates to ONE concentrated patch (a lavender cluster),
  //     while the wasp-mimic hoverfly has no colony to provision and patrols open GROUND for aphid colonies to
  //     lay beside. Neither gate is a flower COUNT any more — that is what made them interchangeable.
  //   • Green Lacewing is a PREDATOR that follows aphids, not blooms: composition (density over real plot
  //     space) with no flower clause at all.
  //   • Crane Fly comes after rain — its leatherjacket grubs live in wet turf, hence rain + dense damp growth.
  //   • Mayfly HATCHES from water and lives a day; `waterCellsMin` is the whole point. Note its streamside
  //     host set is `use: "driver"` and therefore does NOT gate — its gate count comes only from `appears`.
  //   • Hummingbird Hawk-moth is an epic that used to be plainer than several commons (2 gates). As a migrant
  //     day-flier it now wants warm bright weather and a garden already deep in bloom.
  {
    id: "honeybee", name: "Honeybee", icon: "🐝", weight: 34, rarity: "common", value: 8, skittish: 0.34, locomotion: "fly", behavior: "beeline",
    art: { foreShape: "round", hindShape: "round", wing: "#f0b429", wingEdge: "#c88a1a", body: "#241a12",
      pattern: "bands", patternC: "#241a12", span: 0.82, antenna: "club" },
    appears: { day: true, clusterSpecies: "lavender", clusterMin: 3, clusterRadius: 2.5, likesFlowers: 0.34 },   // a TRUE bee: a colony forager recruits nestmates to a single CONCENTRATED patch, so it wants three lavender massed together — where the look-alike Hoverfly asks for no blooms at all (no `lands`). `day` is not optional for a honeybee: it cannot navigate in the dark and the colony stays in the hive overnight.
  },
  {
    id: "hoverfly", name: "Hoverfly", icon: "🪰", weight: 30, rarity: "common", value: 10, skittish: 0.44, locomotion: "fly", behavior: "hoverDash",   // named for it: holds dead still in the air, then snaps a few inches sideways
    art: { foreShape: "round", hindShape: "round", wing: "#f2c73a", wingEdge: "#c89a22", body: "#1a1a1a",
      pattern: "bands", patternC: "#1a1a1a", span: 0.72, antenna: "club" },
    appears: { day: true, minPlots: 6, likesFlowers: 0.3 },   // a wasp-MIMIC fly, not a bee: no colony to provision, so it patrols open GROUND for aphid colonies to lay beside — the deliberate inverse of the Honeybee, which wants blooms massed in one spot. Day-flying like its model, hence `day`.
  },
  {
    id: "lacewing", name: "Green Lacewing", icon: "🦟", weight: 22, rarity: "common", value: 16, skittish: 0.4, locomotion: "fly", behavior: "drifter",
    art: { foreShape: "round", hindShape: "round", wing: "#a6e06a", wingEdge: "#6fae3e", body: "#7fc24a",
      pattern: "veins", patternC: "#6fae3e", span: 0.86, antenna: "club" },
    appears: { densityMin: 0.7, minPlots: 8, likesPlants: 0.12 },   // an aphid PREDATOR: it follows infestations, not blooms, so it reads garden composition — dense growth spread over real ground, with no flower clause at all. NO TIME GATE ON PURPOSE: lacewings are crepuscular-to-nocturnal fliers that also sit about in foliage by day, so neither gate is right.
  },
  {
    id: "cranefly", name: "Crane Fly", icon: "🦟", weight: 20, rarity: "uncommon", value: 18, skittish: 0.5, locomotion: "fly", behavior: "drifter",
    art: { foreShape: "round", hindShape: "round", wing: "#d8cbb0", wingEdge: "#b0a486", body: "#6a5c48",
      pattern: "none", patternC: "#6a5c48", span: 0.9, antenna: "club" },
    appears: { rainMin: RAIN_ANY, densityMin: 0.5, likesPlants: 0.06 },   // its leatherjacket grubs live in wet turf: rain brings the adults drifting up out of dense damp grass, not off open ground. NO TIME GATE ON PURPOSE: craneflies drift about at any hour and famously blunder into lit windows after dark — the rain gate is the real filter.
  },
  {
    id: "mayfly", name: "Mayfly", icon: "🪰", weight: 16, rarity: "uncommon", value: 24, skittish: 0.46, locomotion: "fly", behavior: "drifter",
    art: { foreShape: "pointed", hindShape: "round", wing: "#eef1ea", wingEdge: "#cdd4c8", body: "#c9b98a",
      pattern: "none", patternC: "#c9b98a", span: 0.84, antenna: "club" },
    appears: { day: true, waterCellsMin: 20, likesPlants: 0.05 },   // it HATCHES from the water and lives a single day — real water is the whole point, and its streamside cluster (a driver) only means something beside it
  },
  {
    id: "hawkmoth", name: "Hummingbird Hawk-moth", icon: "🦋", weight: 8, rarity: "rare", value: 70, skittish: 0.6, locomotion: "fly", behavior: "hoverDash",   // hovers at a bloom like a hummingbird, then dashes to the next
    art: { foreShape: "pointed", hindShape: "round", wing: "#8a7d6a", wingEdge: "#d07a3a", body: "#3a3026",
      pattern: "bands", patternC: "#2a2218", span: 1.12, antenna: "feather" },
    appears: { day: true, weather: ["clear"], minFlowering: 6, likesFlowers: 0.22 },   // a migrant day-flying moth that hovers like a hummingbird: it crosses continents for warm settled weather and a garden already deep in bloom. Was 2 gates — plainer than several commons.
  },
  // ── CRAWLERS ──
  // GROUND & CRAWLING insects. The problem here was DUPLICATES, not blandness: Slug/Snail were identical,
  // Weevil/Click Beetle were identical, Crane Fly/Millipede matched, and Tiger Beetle, Azure Damselfly and
  // Common Darter all shared `day + minPlots`. Every one of these 14 now has a signature no other insect on
  // the 70-strong roster shares. Notes a future editor should not undo:
  //   • THE ODONATE SPLIT. Damselfly and Darter breed in WATER; the Tiger Beetle is a dry-ground sprinter.
  //     They were interchangeable and are now near-opposites: damselfly waterCellsMin 15 + cover (a weak flier
  //     that never leaves the margin), darter waterCellsMin 30 + minPlots 14 (breeds in the pond, hunts far
  //     out), tiger beetle bright + minPlots 16 and NO water at all.
  //   • Slug vs Snail: both keep `rainMin` — that is correct for both and must not be "fixed" by removing it
  //     from one. They split on `night`, the naked slug being the nocturnal grazer. This leaves the slug one
  //     gate over the common budget; see the note on its line for why that is deliberate.
  //   • Weevil vs Click Beetle split on where the LARVAE live: the weevil bores into living plants (density,
  //     at night), the click beetle's wireworms live in bare soil (plot area, by day).
  //   • Most of this group has NO host set in hostSets.ts (only aphid/slug/snail/millipede do), so unlike the
  //     butterflies their gate count comes almost entirely from `appears`. Budget accordingly.
  {
    id: "aphid", name: "Aphid", icon: "🐛", weight: 40, rarity: "common", value: 4, skittish: 0.18, locomotion: "crawl", behavior: "sapCling",
    art: { foreShape: "round", hindShape: "round", wing: "#a6d86a", wingEdge: "#6fae3e", body: "#2e4a1e",
      pattern: "none", patternC: "#2e4a1e", span: 0.42 },
    appears: { minPlants: 1, likesPlants: 0.2 },   // infests any plant, and is the roster's plainest condition ON PURPOSE — it is the prey every predator here is gated around. NO TIME GATE ON PURPOSE: it is clamped to a stem sucking sap 24 hours a day and never goes anywhere.
  },
  {
    // SLUG replaces the caterpillar (caterpillars are reserved for the future butterfly-raising feature).
    id: "slug", name: "Garden Slug", icon: "🐌", weight: 30, rarity: "common", value: 10, skittish: 0.1, locomotion: "crawl", behavior: "groundHug",   // a footless mollusc glide — no legs to scuttle on, no lift, just a flowing line
    art: { foreShape: "round", hindShape: "round", wing: "#8a7a5a", wingEdge: "#5a4a34", body: "#4a3e2e",
      pattern: "none", patternC: "#5a4a34", span: 0.72 },
    appears: { night: true, rainMin: RAIN_ANY, likesPlants: 0.12 },   // a NAKED mollusc: no shell to seal, so it grazes on WET NIGHTS only, where the shelled Snail sits out a daytime shower quite happily. Both keep rain — `night` is the split.
    // NOTE: 3 gates (night + rain + its gating "tender leaves" host set) is one over the common budget. Kept
    // deliberately: the Snail already owns the only 2-gate shape left here ([rainMin+HOST]), and the brief is
    // that BOTH molluscs want rain. The alternative was taking rain off one of them, which would be wrong.
  },
  {
    id: "snail", name: "Garden Snail", icon: "🐌", weight: 28, rarity: "common", value: 12, skittish: 0.08, locomotion: "crawl", behavior: "groundHug",   // the same muscular-foot glide as the Slug, carrying its shell flat along the ground
    art: { foreShape: "round", hindShape: "round", wing: "#c8a86a", wingEdge: "#8a6a3a", body: "#9a9086",
      pattern: "none", patternC: "#8a6a3a", span: 0.78 },
    appears: { rainMin: RAIN_ANY, likesPlants: 0.1 },   // a SHELLED mollusc: it can seal up and wait out a dry spell anywhere, so rain alone brings it out — where the naked Slug also needs permanent damp cover. NO TIME GATE ON PURPOSE: that is the Slug/Snail split — the shell is what lets this one graze through a daytime shower.
  },
  {
    id: "millipede", name: "Millipede", icon: "🪱", weight: 24, rarity: "common", value: 14, skittish: 0.14, locomotion: "crawl", behavior: "groundHug",   // hundreds of legs make a smooth wave, not a scuttle: it pours along the soil in S-curves
    art: { foreShape: "round", hindShape: "round", wing: "#4a3a2e", wingEdge: "#2e241c", body: "#c8a24a",
      pattern: "none", patternC: "#2e241c", span: 0.82 },
    appears: { rainMin: RAIN_ANY, decorId: "log", decorMin: 1, likesPlants: 0.06 },   // a detritivore that EATS rotting wood — damp plus dead timber, which is what separates it from the Crane Fly's wet open turf. NO TIME GATE ON PURPOSE: like the Woodlouse it stays under the log, so the hour of day does not change what you would see.
  },
  {
    id: "froghopper", name: "Froghopper", icon: "🪲", weight: 24, rarity: "common", value: 14, skittish: 0.44, locomotion: "crawl", behavior: "hopSkip",
    art: { foreShape: "round", hindShape: "round", wing: "#d8452e", wingEdge: "#9c281a", body: "#1a1210",
      pattern: "bands", patternC: "#1a1210", span: 0.56 },
    appears: { day: true, densityMin: 0.5, likesPlants: 0.1 },   // its nymphs wrap themselves in cuckoo-spit on crowded STEMS, so it wants stem contact rather than a plant count
  },
  {
    id: "earwig", name: "Earwig", icon: "🪳", weight: 22, rarity: "common", value: 16, skittish: 0.34, locomotion: "crawl", behavior: "groundBusy",
    art: { foreShape: "round", hindShape: "round", wing: "#7a4a2a", wingEdge: "#4e2e18", body: "#2a1810",
      pattern: "none", patternC: "#2a1810", span: 0.66 },
    appears: { night: true, decorId: "pebbles", decorMin: 1, likesPlants: 0.06 },   // it spends the day jammed into a CREVICE and comes out after dark — stones are the daytime hiding place
  },
  {
    id: "weevil", name: "Weevil", icon: "🪲", weight: 20, rarity: "common", value: 18, skittish: 0.3, locomotion: "crawl", behavior: "trundle",
    art: { foreShape: "round", hindShape: "round", wing: "#6a5442", wingEdge: "#463424", body: "#2a1e14",
      pattern: "none", patternC: "#2a1e14", span: 0.62 },
    appears: { night: true, densityMin: 0.6, likesPlants: 0.1 },   // a nocturnal borer: it feeds on living stems after dark and wants plants packed tight enough to walk between
  },
  {
    id: "clickBeetle", name: "Click Beetle", icon: "🪲", weight: 18, rarity: "common", value: 22, skittish: 0.4, locomotion: "crawl", behavior: "skitter",
    art: { foreShape: "round", hindShape: "round", wing: "#7a5a3a", wingEdge: "#4e3822", body: "#2a1e14",
      pattern: "none", patternC: "#2a1e14", span: 0.72 },
    appears: { day: true, minPlots: 10, likesPlants: 0.08 },   // its wireworm larvae live in bare SOIL, so it wants ground area rather than greenery — the daylight inverse of the night-feeding Weevil
  },
  {
    id: "centipede", name: "Centipede", icon: "🐛", weight: 16, rarity: "uncommon", value: 28, skittish: 0.5, locomotion: "crawl", behavior: "groundBusy",
    art: { foreShape: "round", hindShape: "round", wing: "#c86a2a", wingEdge: "#9c4a1a", body: "#e8a84a",
      pattern: "none", patternC: "#9c4a1a", span: 0.85 },
    appears: { night: true, rainMin: RAIN_ANY, decorId: "flatBoulder", decorMin: 1 },   // a damp-night hunter that spends the day flattened under a stone slab
  },
  {
    id: "tigerBeetle", name: "Tiger Beetle", icon: "🪲", weight: 10, rarity: "rare", value: 44, skittish: 0.62, locomotion: "crawl", behavior: "skitter",
    art: { foreShape: "round", hindShape: "round", wing: "#3aa06a", wingEdge: "#1f6a44", body: "#1a2420",
      pattern: "spots", patternC: "#eef4e6", span: 0.7 },
    appears: { day: true, weather: ["clear"], minPlots: 16, likesPlants: 0.05 },   // the fastest runner in the garden: it sprints down prey on hot BARE ground, so it is the deliberate opposite of the two water-breeding odonates
  },
  {
    id: "geodeTrapjaw", name: "Geode Trapjaw", icon: "🪨", weight: 8, rarity: "rare", value: 60, skittish: 0.5, locomotion: "crawl", behavior: "ambush",
    art: { foreShape: "round", hindShape: "round", wing: "#8a8478", wingEdge: "#5c574e", body: "#241f2e",
      pattern: "none", patternC: "#241f2e", span: 0.7, glow: "#c69bff" },
    appears: { day: true, densityMin: 0.8, minPlants: 6, likesPlants: 0.08 },   // an ambusher that survives by looking like a stone: it needs cover to be a plausible rock in, and an established garden to have stones lying about at all
  },
  // ── HOVERERS ──
  {
    id: "damselfly", name: "Azure Damselfly", icon: "🌸", weight: 16, rarity: "uncommon", value: 32, skittish: 0.56, locomotion: "hover", behavior: "zigzag",   // the slender darter frame on weak wings: short flits that change heading every hop
    art: { foreShape: "round", hindShape: "round", wing: "#4a9ae8", wingEdge: "#2f6ec4", body: "#1a2438",
      pattern: "bands", patternC: "#1a2438", span: 0.82 },
    appears: { day: true, waterCellsMin: 15, densityMin: 0.4, likesPlants: 0.06 },   // a weak-flying odonate that never leaves the waterside vegetation it emerged from — the smallest water ask on the roster, but it wants cover with it
  },
  {
    id: "robberfly", name: "Robber Fly", icon: "🪰", weight: 10, rarity: "rare", value: 46, skittish: 0.66, locomotion: "hover", behavior: "ambush",
    art: { foreShape: "round", hindShape: "round", wing: "#6a5a48", wingEdge: "#463a2c", body: "#241c14",
      pattern: "none", patternC: "#241c14", span: 0.86 },
    appears: { day: true, weather: ["clear"], densityMin: 0.7, likesPlants: 0.07 },   // an aerial ambush predator: it perches in cover and takes other insects on the wing, so it needs warm still air and prey-rich growth
  },
  {
    id: "darter", name: "Common Darter", icon: "🛩️", weight: 12, rarity: "rare", value: 40, skittish: 0.6, locomotion: "hover", behavior: "zigzag",   // a darter: short straight sorties with hard corners, never a glide
    art: { foreShape: "round", hindShape: "round", wing: "#e0552a", wingEdge: "#b0381a", body: "#2a1a14",
      pattern: "none", patternC: "#2a1a14", span: 0.9 },
    appears: { day: true, waterCellsMin: 30, minPlots: 14, likesPlants: 0.06 },   // a true dragonfly: it breeds in the pond but hunts far out over OPEN ground, so it wants more water than the Damselfly and room to patrol
  },
];

const BY_ID = new Map(INSECTS.map((b) => [b.id, b]));
export function insectDef(id: string): InsectDef | undefined { return BY_ID.get(id); }

/** Weighted-random species pick given a 0..1 roll (over the WHOLE roster, ignoring conditions). */
export function pickInsect(roll: number): InsectDef {
  const pool = INSECTS.filter(isIn);   // (a content release that is not out yet — sim/releases.ts)
  const total = pool.reduce((s, b) => s + b.weight, 0);
  let x = roll * total;
  for (const b of pool) { if ((x -= b.weight) <= 0) return b; }
  return pool[0] ?? INSECTS[0]!;
}

/**
 * STANDARDIZED SPAWN-LIKELIHOOD SCORER. Returns the insect's effective spawn weight in the given garden
 * context PLUS a human-readable breakdown (for the Dev Menu spawn debugger). One formula, no scattered magic:
 *
 *   eligible  = every GATE passes (night/day/rain/weather/minFlowering/minPlants/minPlots/flowers-present)
 *   weight    = eligible ? base · driverScale : 0
 *   driverScale = product of each satisfied DRIVER's factor:
 *                   likesFlowers      → 1 + likesFlowers·min(floweringCount, CAP_FLOWERS)
 *                   likesHostFlowers  → 1 + likesHostFlowers·min(hostCount,  CAP_HOSTS)
 *                   likesPlants       → 1 + likesPlants·min(plantCount,      CAP_PLANTS)
 *
 * So "more qualifying plants → more likely" is exactly one linear-with-soft-cap term per driver, and the
 * `reasons` array shows which gates passed/failed and what each driver contributed.
 */
export interface SpawnScore {
  weight: number;                    // effective spawn weight (0 = can't appear now)
  eligible: boolean;                 // did every gate pass
  base: number;                      // def.weight
  driverScale: number;               // the total soft multiplier applied
  reasons: string[];                 // per-clause breakdown, e.g. "night ✓", "flowers 3 → ×1.9", "minPlots 2 ✗ (have 1)"
}

/**
 * GLOBAL SPAWN CONFIG — the tunable knobs that govern the WHOLE spawn system (not per-insect). Admin-editable
 * from the Insect Odds modal, stored in the `insectSpawn` live-tuning key, installed via setSpawnConfig(). All
 * fields have sensible defaults; the modal shows + edits them with explanations.
 */
export type Rarity = "common" | "uncommon" | "rare" | "epic" | "legendary";
export const RARITIES: Rarity[] = ["common", "uncommon", "rare", "epic", "legendary"];

export interface SpawnConfig {
  gapMinS: number;      // min seconds between spawn ATTEMPTS
  gapMaxS: number;      // max seconds between spawn attempts (actual gap is random in [min,max])
  capBase: number;      // active-cap = clamp(capBase .. capMax, capBase + plots·perPlot)
  capPerPlot: number;   // extra active-cap capacity per this-many plot cells (as a fraction: 1/3 → +1 per 3 plots)
  capMax: number;       // hard ceiling on concurrent insects
  capFlowers: number;   // soft cap on the `likesFlowers` driver (flowering plants counted)
  capHosts: number;     // soft cap on the `likesHostFlowers` driver
  capPlants: number;    // soft cap on the `likesPlants` driver
  // ── VISIT DURATION (global) — how long an insect stays before it leaves ──
  lifeMinS: number;     // min seconds an insect lingers in the garden
  lifeMaxS: number;     // max seconds (actual lifespan is random in [min,max])
  // ── DIRECT-% MODEL — each insect has a BASE % (its per-attempt chance in a neutral garden, no drivers).
  //    Drivers raise it toward the tier's soft ceiling with diminishing returns; the base % is exactly what you set.
  /** Per-rarity DEFAULT base % (0..1) — the starting per-attempt chance for a tier, before any per-insect edit. */
  tierBasePct: Record<Rarity, number>;
  /** Per-rarity SOFT CEILING (0..1) the DRIVERS asymptote toward (diminishing returns). The base % is exact and
   *  can even sit above this ceiling if you set it high; the ceiling only shapes how far DRIVERS push it up. */
  tierCap: Record<Rarity, number>;
  /** Driver climb difficulty: how quickly drivers approach the ceiling. LOW = drivers reach the ceiling fast;
   *  HIGH = drivers give steep diminishing returns (default 6). */
  capSoftness: number;
}
/**
 * DEFAULTS — tuned so a mature garden reads as INHABITED, not INFESTED.
 *
 * The population is a queue: one attempt every `gap` seconds on average, each surviving `life` seconds, with
 * attempts SKIPPED while at cap. In any garden with a few flowers Σ(per-attempt chances) saturates the
 * `min(1, Σp)` in pickEligibleInsect, so essentially every attempt lands a bug. Free-running occupancy is
 * therefore ≈ residency/gap, and the cap only binds once that exceeds it.
 *
 * RESIDENCY IS LONGER THAN `lifeMaxS` SUGGESTS. A bug at its lifespan only starts leaving when it is NOT
 * resting (InsectLayer step()), and rests average ~12.5s (restDuration), so an expiry landing mid-rest waits
 * ~6s more, plus the 0.8s leave fade. Effective residency ≈ 40s life + ~5s ≈ 45s. Do not "simplify" the
 * steady-state math back to lifeavg/gapavg — it understates occupancy by about 12%.
 *
 * THE OLD NUMBERS (gap 6-16 → ~11s avg, capMax 8) gave free occupancy ≈ 45/11 ≈ 4.1 — but the REAL problem
 * was the refill rate at the ceiling, not the free number: at cap, a departure opened a slot that the next
 * attempt (~11s away, ~4× faster than the ~45s drain) filled almost immediately, so a large garden sat PINNED
 * at 8 rather than fluctuating below it. Steady occupancy, not any one species' weight, is what read as
 * "the place is crawling".
 *
 * THE NEW NUMBERS: gap 14-30 (~22s avg) halves the refill rate, so free occupancy ≈ 45/22 ≈ 2.0 and the cap
 * stops being a floor the population rests on — it becomes a true ceiling that only a driver-rich garden
 * approaches. capMax 6 lowers that ceiling; capPerPlot 1/5 makes the climb to it cost more garden.
 *
 *   plots   cap (base 2 + plots/5, ≤6)   expected steady active
 *    4      2                            ~2    (cap binds at capBase; a new garden is UNCHANGED from before)
 *   12      4                            ~2.0  (free-running; the cap has stopped binding)
 *   20      6  (max, first reached here) ~2.0  (free-running, occasional excursion toward 6)
 *   50      6                            ~2.0-2.5 (drivers push Σp and visits up; peaks touch the ceiling)
 *
 * Under the OLD numbers those same gardens sat at 2 / 4.1 / 4.1 / 4.1 with the ceiling pinned at 8.
 *
 * Roughly half the old steady-state at every size, WITHOUT touching any species' weight: a common cabbage
 * white is exactly as likely to be the bug you see, there are simply fewer bugs out at once.
 */
export const DEFAULT_SPAWN_CONFIG: SpawnConfig = {
  gapMinS: 14, gapMaxS: 30, capBase: 2, capPerPlot: 1 / 5, capMax: 6,
  capFlowers: 12, capHosts: 8, capPlants: 20,
  lifeMinS: 25, lifeMaxS: 55,
  tierBasePct: { common: 0.4, uncommon: 0.22, rare: 0.12, epic: 0.06, legendary: 0.02 },
  tierCap:     { common: 1, uncommon: 0.8, rare: 0.5, epic: 0.3, legendary: 0.15 },
  capSoftness: 6,
};
/**
 * The smallest value each numeric knob may take — the Insect Odds modal's own `min` on each input. Enforced HERE
 * because `<input min>` only steers the spinner: an emptied box reaches onChange as 0, and a `0` gap means a
 * spawn attempt every frame. A value that is not a finite number at all (a string from a hand-edited blob, a
 * null) keeps the default instead — `"14" + roll * 16` is string concatenation, not a gap.
 */
const SPAWN_CONFIG_MIN: Record<Exclude<keyof SpawnConfig, "tierBasePct" | "tierCap">, number> = {
  gapMinS: 1, gapMaxS: 1, capBase: 0, capPerPlot: 0, capMax: 1,
  capFlowers: 1, capHosts: 1, capPlants: 1, lifeMinS: 1, lifeMaxS: 1, capSoftness: 1,
};
let SPAWN_CONFIG: SpawnConfig = { ...DEFAULT_SPAWN_CONFIG };
export function setSpawnConfig(cfg: Partial<SpawnConfig> | undefined): void {
  const o: Record<string, unknown> = isPlainObject(cfg) ? cfg : {};
  const next = { ...DEFAULT_SPAWN_CONFIG };
  for (const k of Object.keys(SPAWN_CONFIG_MIN) as Array<keyof typeof SPAWN_CONFIG_MIN>) {
    next[k] = finiteAtLeast(o[k], SPAWN_CONFIG_MIN[k], DEFAULT_SPAWN_CONFIG[k]);
  }
  // A range whose max sits under its min is read as "exactly the min" rather than a negative span.
  next.gapMaxS = Math.max(next.gapMaxS, next.gapMinS);
  next.lifeMaxS = Math.max(next.lifeMaxS, next.lifeMinS);
  next.tierBasePct = coerceTierTable(o.tierBasePct, DEFAULT_SPAWN_CONFIG.tierBasePct);
  next.tierCap = coerceTierTable(o.tierCap, DEFAULT_SPAWN_CONFIG.tierCap);
  SPAWN_CONFIG = next;
}
export function getSpawnConfig(): SpawnConfig { return SPAWN_CONFIG; }

/** A random insect LIFESPAN (seconds) from the global config — how long a spawned bug lingers before leaving. */
export function insectLifespanS(roll = Math.random()): number {
  const cfg = SPAWN_CONFIG;
  return cfg.lifeMinS + roll * Math.max(0, cfg.lifeMaxS - cfg.lifeMinS);
}


/** Count flowering plants matching a `flowers` host list (used for the host gate + driver). */
function hostMatchCount(flowers: string[] | undefined, ctx: AppearCtx): number {
  if (!flowers || !flowers.length) return 0;
  let n = 0;
  for (const f of flowers) if (ctx.species.has(f)) n++;   // species set is FLOWERING species; counts distinct hosts present
  return n;
}

/** The biggest CLUSTER of a given plant species: for each plant of that species, count how many plants of the
 *  same species (incl. itself) fall within `radius` garden-units. The max is the largest tight clump. This is a
 *  neighborhood-count approximation of a clique (cheap, and reads correctly for "N within R of each other"). */
function biggestCluster(plants: Array<{ species: string; gx: number; gy: number }>, species: string, radius: number): number {
  const pts = plants.filter((p) => p.species === species);
  if (pts.length <= 1) return pts.length;
  const r2 = radius * radius;
  let best = 1;
  for (let i = 0; i < pts.length; i++) {
    let n = 1;   // itself
    for (let j = 0; j < pts.length; j++) {
      if (i === j) continue;
      const dx = pts[i]!.gx - pts[j]!.gx, dy = pts[i]!.gy - pts[j]!.gy;
      if (dx * dx + dy * dy <= r2) n++;
    }
    if (n > best) best = n;
  }
  return best;
}

/**
 * ADMIN CONDITION OVERRIDES — a per-insect override of `appears`, editable from the Insect Odds modal and
 * stored in the `insectConditions` live-tuning key. When an override exists for an id, it REPLACES the source
 * `appears` entirely (so an admin can fully re-author a species' conditions). Installed by the sim from tuning
 * via setInsectConditionOverrides(); the scorer reads it through effectiveAppears().
 *
 * AN OVERRIDE SILENTLY WINS OVER THIS FILE. Conditions are TWO layers: the `appears` blocks above are the
 * DEFAULTS that ship in the bundle; these overrides live in Postgres and are written whenever an admin saves a
 * species in the Insect Odds modal. Because effectiveAppears() replaces rather than merges, an overridden id
 * also loses the INSECT_HOST_SETS attractors merged in on the no-override path — and EDITING ITS `appears`
 * BLOCK CHANGES NOTHING IN THE GAME, with no error, no log and no clue in the diff. Before trusting a
 * condition edit, check `node scripts/tune.mjs get` (and again with `--staging` — separate database). As of
 * 2026-09-01 the key exists on NEITHER server, so today every insect condition comes from this file.
 * CONDITIONS.md §2 has the precedence rules and how to un-pin an id.
 */
export type InsectConditionOverrides = Record<string, AppearCond | null>;   // id → cond (null = "no conditions")
/** AppearCond's non-numeric fields, for `coerceCondMap` — every other field is a number. */
const APPEAR_COND_SPEC: CondSpec = {
  bools: ["night", "day"],
  strings: ["speciesId", "clusterSpecies", "decorId"],
  stringArrays: ["weather", "flowers"],
};
let CONDITION_OVERRIDES: InsectConditionOverrides = {};
export function setInsectConditionOverrides(map: InsectConditionOverrides | undefined): void {
  CONDITION_OVERRIDES = coerceCondMap<AppearCond>(map, APPEAR_COND_SPEC);
}
export function getInsectConditionOverrides(): InsectConditionOverrides { return CONDITION_OVERRIDES; }
/** The EFFECTIVE appear-condition for an insect: an admin override (if present) wins over the source default. */
export function effectiveAppears(def: InsectDef): AppearCond | undefined {
  if (Object.prototype.hasOwnProperty.call(CONDITION_OVERRIDES, def.id)) {
    const o = CONDITION_OVERRIDES[def.id];
    return o ?? undefined;   // null override ⇒ explicitly "no conditions" (admin cleared it)
  }
  return authoredAppears(def);
}
/** The AUTHORED appear-condition — what this file ships, ignoring any live override: the source `appears` with
 *  the catalogue-wide HOST-SETS merged in (default attractors). The dev override report compares against it. */
export function authoredAppears(def: InsectDef): AppearCond | undefined {
  const host = INSECT_HOST_SETS[def.id];
  if (!host?.length) return def.appears;
  const sets = withHostSets(def.appears?.sets, host);
  return { ...(def.appears ?? {}), ...(sets ? { sets } : {}) };
}

/**
 * ADMIN RARITY OVERRIDES — a per-insect override of the RARITY tier and/or a direct BASE % (0..1), editable from
 * the Insect Odds modal and stored in the `insectRarity` live-tuning key. Installed via setInsectRarityOverrides().
 * `basePct` is the neutral-garden per-attempt chance for this insect; absent ⇒ the tier's default base %.
 */
export type InsectRarityOverrides = Record<string, { rarity?: Rarity; basePct?: number } | null>;
let RARITY_OVERRIDES: InsectRarityOverrides = {};
export function setInsectRarityOverrides(map: InsectRarityOverrides | undefined): void {
  RARITY_OVERRIDES = coerceRarityMap(map, RARITIES);
}
export function getInsectRarityOverrides(): InsectRarityOverrides { return RARITY_OVERRIDES; }

/** Infer a rarity tier from a source `def.weight`. DEAD FOR THE SHIPPED ROSTER — all 70 insects declare
 *  `rarity` explicitly, so this only ever fires for a def written without one. Kept as the fallback so a new
 *  species that forgets the field still gets a tier rather than crashing; do not re-derive tiers from weights. */
export function inferRarity(weight: number): Rarity {
  if (weight >= 26) return "common";
  if (weight >= 15) return "uncommon";
  if (weight >= 9) return "rare";
  if (weight >= 5) return "epic";
  return "legendary";
}
/** The EFFECTIVE rarity tier for an insect: admin override → def.rarity → inferred from its source weight. */
export function rarityOf(def: InsectDef): Rarity {
  const o = RARITY_OVERRIDES[def.id];
  if (o && o.rarity) return o.rarity;
  return def.rarity ?? inferRarity(def.weight);
}

/** Resolved CATCH properties for the hold-to-track minigame. `escapeTime` (base seconds of pursuit) and
 *  `captureResistance` (0..1 slowing Capture Chance) are authored per-insect OR derived from rarity — so a
 *  rarer bug gives you less time AND builds odds slower, but never has to be impossibly fast. */
export interface CatchProps { escapeTime: number; captureResistance: number; }
export const RARITY_CATCH: Record<Rarity, CatchProps> = {
  common:    { escapeTime: 6.0, captureResistance: 0.0 },
  uncommon:  { escapeTime: 5.5, captureResistance: 0.12 },
  rare:      { escapeTime: 5.0, captureResistance: 0.28 },
  epic:      { escapeTime: 4.5, captureResistance: 0.42 },
  legendary: { escapeTime: 4.0, captureResistance: 0.55 },
};
export function catchProps(def: InsectDef): CatchProps {
  const d = RARITY_CATCH[rarityOf(def)];
  return {
    escapeTime: def.escapeTime ?? d.escapeTime,
    captureResistance: def.captureResistance ?? d.captureResistance,
  };
}

/**
 * The Capture Chance % gained PER SECOND for this bug given the player's kit capture-rate (%/s). The bug's
 * `captureResistance` scales it down: effectiveRate = kitRate · (1 − resistance). This is the core knob —
 * a rare bug's resistance lowers your practical max (rate × time), which better gear overcomes.
 */
export function captureRatePerSec(def: InsectDef, kitRatePct: number, resistPierce = 0): number {
  // resistPierce (a talent, 0..1) shaves the bug's captureResistance so rare bugs build odds faster.
  const resistance = Math.max(0, catchProps(def).captureResistance * (1 - Math.max(0, Math.min(1, resistPierce))));
  return Math.max(0.5, kitRatePct * (1 - resistance));
}
/** The EFFECTIVE BASE % (0..1) for an insect in a NEUTRAL garden: admin basePct override → the tier's default
 *  base % (config.tierBasePct). This is exactly the chance when no drivers apply. */
export function baseChanceOf(def: InsectDef): number {
  const o = RARITY_OVERRIDES[def.id];
  if (o && typeof o.basePct === "number") return Math.max(0, Math.min(1, o.basePct));
  return SPAWN_CONFIG.tierBasePct[rarityOf(def)] ?? 0.1;
}

export function insectSpawnWeight(def: InsectDef, ctx: AppearCtx): SpawnScore {
  const base = baseChanceOf(def);          // the insect's BASE % (0..1) in a neutral garden
  const c = effectiveAppears(def);
  const reasons: string[] = [];
  if (!c) return { weight: base, eligible: true, base, driverScale: 1, reasons: [`base ${(base * 100).toFixed(1)}%`] };

  let eligible = true;
  const gateFail = (msg: string) => { eligible = false; reasons.push(msg); };

  // ── GATES ──
  if (c.night) (ctx.night ? reasons.push("night ✓") : gateFail("night ✗"));
  if (c.day) (!ctx.night ? reasons.push("day ✓") : gateFail("day ✗ (night)"));
  if (c.rainMin != null) (ctx.rain >= c.rainMin ? reasons.push(`rain≥${c.rainMin} ✓`) : gateFail(`rain≥${c.rainMin} ✗ (${ctx.rain.toFixed(2)})`));
  if (c.weather) (c.weather.includes(ctx.weather) ? reasons.push(`weather ${ctx.weather} ✓`) : gateFail(`weather ✗ (${ctx.weather})`));
  if (c.minFlowering != null) (ctx.floweringCount >= c.minFlowering ? reasons.push(`≥${c.minFlowering} flowering ✓`) : gateFail(`≥${c.minFlowering} flowering ✗ (${ctx.floweringCount})`));
  if (c.minPlants != null) (ctx.plantCount >= c.minPlants ? reasons.push(`≥${c.minPlants} plants ✓`) : gateFail(`≥${c.minPlants} plants ✗ (${ctx.plantCount})`));
  if (c.minPlots != null) (ctx.plotCount >= c.minPlots ? reasons.push(`≥${c.minPlots} plots ✓`) : gateFail(`≥${c.minPlots} plots ✗ (${ctx.plotCount})`));
  const hosts = hostMatchCount(c.flowers, ctx);
  if (c.flowers) (hosts > 0 ? reasons.push(`host flower ✓`) : gateFail(`needs one of [${c.flowers.join(",")}] ✗`));

  // ── ADVANCED GATES (spatial / composition) ──
  if (c.speciesId && c.speciesMin != null) {
    const have = ctx.speciesCounts[c.speciesId] ?? 0;
    (have >= c.speciesMin ? reasons.push(`≥${c.speciesMin} ${c.speciesId} ✓`) : gateFail(`≥${c.speciesMin} ${c.speciesId} ✗ (${have})`));
  }
  if (c.clusterSpecies && c.clusterMin != null && c.clusterRadius != null) {
    const best = biggestCluster(ctx.plantPos, c.clusterSpecies, c.clusterRadius);
    (best >= c.clusterMin ? reasons.push(`${c.clusterMin}× ${c.clusterSpecies} within ${c.clusterRadius} ✓`) : gateFail(`${c.clusterMin}× ${c.clusterSpecies} within ${c.clusterRadius} ✗ (max ${best})`));
  }
  if (c.waterCellsMin != null) (ctx.waterCells >= c.waterCellsMin ? reasons.push(`≥${c.waterCellsMin} water ✓`) : gateFail(`≥${c.waterCellsMin} water ✗ (${ctx.waterCells})`));
  if (c.decorId && c.decorMin != null) {
    const have = ctx.decorCounts[c.decorId] ?? 0;
    (have >= c.decorMin ? reasons.push(`≥${c.decorMin} ${c.decorId} ✓`) : gateFail(`≥${c.decorMin} ${c.decorId} ✗ (${have})`));
  }
  if (c.densityMin != null) {
    const density = ctx.plotCount > 0 ? ctx.plantCount / ctx.plotCount : 0;
    (density >= c.densityMin ? reasons.push(`density≥${c.densityMin} ✓`) : gateFail(`density≥${c.densityMin} ✗ (${density.toFixed(2)})`));
  }

  // ── REQUIRE-SETS (unified attractor gates) ──
  const reqCtx: ReqCtx = { speciesCounts: ctx.speciesCounts, decorCounts: ctx.decorCounts, plantPos: ctx.plantPos };
  const sets = scoreRequireSets(c.sets, reqCtx, SPAWN_CONFIG.capHosts);
  reasons.push(...sets.reasons);
  if (!sets.eligible) eligible = false;

  if (!eligible) return { weight: 0, eligible: false, base, driverScale: 0, reasons };

  // ── DRIVERS (soft multipliers) — soft caps from the global spawn config ──
  let scale = sets.driver;   // extra completed copies of each require-set nudge odds up
  if (c.likesFlowers) { const f = 1 + c.likesFlowers * Math.min(ctx.floweringCount, SPAWN_CONFIG.capFlowers); scale *= f; reasons.push(`flowers ${ctx.floweringCount} → ×${f.toFixed(2)}`); }
  if (c.likesHostFlowers) { const f = 1 + c.likesHostFlowers * Math.min(hosts, SPAWN_CONFIG.capHosts); scale *= f; reasons.push(`hosts ${hosts} → ×${f.toFixed(2)}`); }
  if (c.likesPlants) { const f = 1 + c.likesPlants * Math.min(ctx.plantCount, SPAWN_CONFIG.capPlants); scale *= f; reasons.push(`plants ${ctx.plantCount} → ×${f.toFixed(2)}`); }

  return { weight: base * scale, eligible: true, base, driverScale: scale, reasons };
}

/** Does this insect's condition pass right now (eligible = weight > 0)? Convenience over insectSpawnWeight. */
export function insectEligible(def: InsectDef, ctx: AppearCtx): boolean {
  return insectSpawnWeight(def, ctx).weight > 0;
}

/**
 * The MAX number of insects allowed active at once, scaled by purchased PLOT cells (garden space). Driven by
 * the global spawn config: clamp(capBase .. capMax, capBase + plots·capPerPlot). This is the concurrency cap
 * the sim spawns up to.
 */
export function activeInsectCap(plotCount: number): number {
  const cfg = SPAWN_CONFIG;
  return Math.max(cfg.capBase, Math.min(cfg.capMax, cfg.capBase + Math.floor(plotCount * cfg.capPerPlot)));
}

/** A random spawn-ATTEMPT gap (seconds), from the global config. The sim waits this long between attempts. */
export function spawnGapS(roll = Math.random()): number {
  const cfg = SPAWN_CONFIG;
  return cfg.gapMinS + roll * (cfg.gapMaxS - cfg.gapMinS);
}

/**
 * SOFT-CAP curve — a TWO-STAGE diminishing-returns curve on `raw` (= weight/denom):
 *
 *   • BELOW the cap: a gentle saturating approach — chance eases toward the cap with mild diminishing returns,
 *     but reaching the cap is NOT ridiculously hard. `raw == RAW_AT_CAP` lands you exactly at `cap` (RAW_AT_CAP
 *     = cap · APPROACH_MULT, so e.g. ~2.5× the "linear" weight gets you to the cap).
 *   • ABOVE the cap: a much STEEPER roll-off (governed by `soften`) — extra weight still raises the chance but
 *     each bit buys far less, curving toward (never reaching) 1. So going past the cap is deliberately harder.
 *
 * Continuous at the cap. `soften` (config.capSoftness): LOW = hard knee past the cap, HIGH = gentler.
 */
/**
 * DRIVER CURVE — the DIRECT-% model. `base` is the neutral-garden chance (exactly what the admin set). Drivers
 * (`driverScale` ≥ 1) raise it toward the `ceiling` with DIMINISHING RETURNS:
 *
 *     bonus   = base · (driverScale − 1)          (the raw extra chance drivers want to add)
 *     chance  = base + (ceiling − base) · (1 − e^(−bonus / ((ceiling − base) · soften)))
 *
 * At driverScale = 1 → chance = base exactly. As drivers grow, chance asymptotes toward `ceiling` (never
 * reaching it). If `base` is already ≥ `ceiling` (admin set it high on purpose), chance = base (drivers add
 * nothing beyond — the admin's explicit value wins). `soften` controls how fast the climb approaches the ceiling.
 */
export function driverCurve(base: number, driverScale: number, ceiling: number, soften: number): number {
  const b = Math.max(0, Math.min(1, base));
  if (driverScale <= 1 || b >= ceiling) return b;
  const bonus = b * (driverScale - 1);
  const room = ceiling - b;
  const k = Math.max(0.01, soften);
  return b + room * (1 - Math.exp(-bonus / (room * k)));
}

/** Expected play SECONDS until an event of per-attempt probability `chance` occurs = (1/chance)·avgGap. */
export function expectedSecondsToAppear(chance: number): number {
  if (chance <= 0) return Infinity;
  const avgGap = (SPAWN_CONFIG.gapMinS + SPAWN_CONFIG.gapMaxS) / 2;
  return (1 / chance) * avgGap;
}

/**
 * The REALIZED per-attempt chance a species actually spawns, accounting for COMPETITION with every other
 * eligible species. The spawn model spawns SOMETHING with probability min(1, Σp) then picks proportionally, so:
 *   • if Σp ≤ 1  → realized = the species' own chance (nobody competes; base == adjusted)
 *   • if Σp > 1  → realized = chance · (1/Σp)  (oversubscribed → everyone is scaled down proportionally)
 * `chance` is this species' own absolute chance; `totalChance` is Σp over all eligible species (unclamped).
 */
export function realizedChance(chance: number, totalChance: number): number {
  if (chance <= 0) return 0;
  return totalChance > 1 ? chance / totalChance : chance;
}
/** Σ of every insect's absolute per-attempt chance in `ctx` (UNCLAMPED — can exceed 1). Drives realizedChance. */
export function totalRawSpawnChance(ctx: AppearCtx): number {
  return INSECTS.reduce((s, def) => s + insectAbsoluteChance(def, ctx), 0);
}

/**
 * The ABSOLUTE per-attempt spawn chance for one insect (0..1) — DIRECT-% model. In a neutral garden the chance
 * is the insect's BASE % exactly; garden DRIVERS raise it toward the tier ceiling with diminishing returns (see
 * driverCurve). Independent of what else is eligible, so a rare bug stays rare even when it's the only option
 * (the leftover probability = "spawn nothing this attempt").
 */
export function insectAbsoluteChance(def: InsectDef, ctx: AppearCtx): number {
  if (isOut(def)) return 0;   // never spawns naturally (a content release that is not out yet — sim/releases.ts)
  const s = insectSpawnWeight(def, ctx);
  if (!s.eligible || s.base <= 0) return 0;
  const ceiling = SPAWN_CONFIG.tierCap[rarityOf(def)] ?? 1;
  return driverCurve(s.base, s.driverScale, ceiling, SPAWN_CONFIG.capSoftness);
}

/**
 * NATURAL spawn pick using ABSOLUTE per-attempt chances (not pool-normalized). Each eligible insect has its own
 * chance `pi`; the attempt spawns SOMETHING with probability min(1, Σpi), and if so picks among them in
 * proportion to `pi`. Returns null (= spawn nothing this attempt) otherwise — so even if only a legendary is
 * eligible, it spawns only ~its own chance of the time, never every attempt.
 */
export function pickEligibleInsect(ctx: AppearCtx, roll: number): InsectDef | null {
  const scored = INSECTS.map((b) => ({ b, p: insectAbsoluteChance(b, ctx) })).filter((s) => s.p > 0);
  if (!scored.length) return null;
  const sum = scored.reduce((s, x) => s + x.p, 0);
  const pSpawn = Math.min(1, sum);          // chance SOMETHING spawns this attempt
  if (roll >= pSpawn) return null;          // ← the "spawn nothing" branch (keeps rares from spamming)
  // something spawns: pick proportional to each insect's own chance.
  let x = (roll / pSpawn) * sum;
  for (const s of scored) { if ((x -= s.p) <= 0) return s.b; }
  return scored[scored.length - 1]!.b;
}

/**
 * Full per-species scoring for the debugger. `chance` = the insect's ABSOLUTE per-attempt spawn chance (0..1)
 * — the same number the sim uses — NOT a pool share. Sorted by chance desc; ineligible species show chance 0
 * with the reason. Also returns the total per-attempt spawn probability so the modal can show "nothing" odds.
 */
export function scoreRoster(ctx: AppearCtx): Array<{ def: InsectDef; score: SpawnScore; chance: number; rarity: Rarity }> {
  const rows = INSECTS.map((def) => ({ def, score: insectSpawnWeight(def, ctx), chance: insectAbsoluteChance(def, ctx), rarity: rarityOf(def) }));
  return rows.sort((a, b) => b.chance - a.chance || b.score.weight - a.score.weight);
}

/** The total per-attempt probability that SOMETHING spawns (Σ absolute chances, capped at 1). 1 − this = the
 *  chance an attempt spawns NOTHING. */
export function totalSpawnChance(ctx: AppearCtx): number {
  const sum = INSECTS.reduce((s, def) => s + insectAbsoluteChance(def, ctx), 0);
  return Math.min(1, sum);
}

/**
 * SPAWN SIMULATOR — run `attempts` independent spawn attempts against the current settings + garden context and
 * tally what actually spawned, WITHOUT touching the live game. Returns per-species observed counts + the
 * EXPECTED count (attempts × that species' absolute chance), so the modal can compare actual vs. expected and
 * confirm the system behaves as configured. Deterministic when a seeded `rng` is passed.
 */
export interface SimResult {
  attempts: number;
  spawned: number;              // attempts that produced ANY insect
  nothing: number;              // attempts that spawned nothing
  perSpecies: Array<{ id: string; name: string; icon: string; rarity: Rarity; observed: number; expected: number; chance: number }>;
}
export function simulateSpawns(ctx: AppearCtx, attempts: number, rng: () => number = Math.random): SimResult {
  const chances = INSECTS.map((def) => ({ def, p: insectAbsoluteChance(def, ctx) }));
  const observed: Record<string, number> = {};
  let spawned = 0;
  for (let i = 0; i < attempts; i++) {
    const roll = rng();
    const picked = pickAbsolute(chances, roll);
    if (picked) { observed[picked.id] = (observed[picked.id] ?? 0) + 1; spawned++; }
  }
  // `expected` uses the REALIZED per-attempt rate (competition-adjusted: p·min(1,Σ)/Σ), matching how the sim
  // shares spawns when the garden is oversubscribed (Σ>1) — otherwise it inflates expected vs. observed.
  const live = chances.filter((c) => c.p > 0);
  const sum = live.reduce((s, x) => s + x.p, 0);
  const realizeK = sum > 0 ? Math.min(1, sum) / sum : 0;
  const perSpecies = live
    .map((c) => ({ id: c.def.id, name: c.def.name, icon: c.def.icon, rarity: rarityOf(c.def), observed: observed[c.def.id] ?? 0, expected: attempts * c.p * realizeK, chance: c.p }))
    .sort((a, b) => b.observed - a.observed || b.expected - a.expected);
  return { attempts, spawned, nothing: attempts - spawned, perSpecies };
}
/** One attempt over precomputed absolute chances (shared by simulate + the sim's pick). */
function pickAbsolute(chances: Array<{ def: InsectDef; p: number }>, roll: number): InsectDef | null {
  const live = chances.filter((c) => c.p > 0);
  if (!live.length) return null;
  const sum = live.reduce((s, x) => s + x.p, 0);
  const pSpawn = Math.min(1, sum);
  if (roll >= pSpawn) return null;
  let x = (roll / pSpawn) * sum;
  for (const c of live) { if ((x -= c.p) <= 0) return c.def; }
  return live[live.length - 1]!.def;
}

// ---- lifecycle timings (seconds) ----
/** How long a butterfly flits about before it looks for a flower to land on. */
export const FLY_MIN_S = 5;
export const FLY_MAX_S = 12;
/** How long it rests on a flower (clickable window) before taking off again. Skittish → shorter, but every
 *  butterfly now lingers a good while so the player has time to spot + collect it. */
export function restDuration(skittish: number): number {
  return 16 - skittish * 7;      // calm ≈16s → skittish ≈9s
}
/** After landing + resting, it flies again; after a couple of cycles it eventually leaves the garden. */
export const MAX_VISITS = 3;
/** Defensive coerce of the persisted per-species caught tally (butterflyId → count). CUMULATIVE — this is
 *  the lifetime catch count and nothing subtracts from it; selling writes `insectSold` instead. */
export function coerceButterfliesCaught(raw: unknown): Record<string, number> {
  const out: Record<string, number> = {};
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return out;
  for (const [k, v] of Object.entries(raw as Record<string, unknown>)) {
    if (BY_ID.has(k) && typeof v === "number" && v > 0) out[k] = Math.floor(v);
  }
  return out;
}

/** Defensive coerce of the persisted per-species SOLD tally (insectId → count). Same shape and rules as
 *  `coerceButterfliesCaught`; kept separate so the two never get wired to the same validator by accident. */
export function coerceInsectSold(raw: unknown): Record<string, number> {
  return coerceButterfliesCaught(raw);
}

/** The SELLABLE bag: caught minus sold, per species, with the emptied species dropped. Selling must never
 *  touch `butterfliesCaught` — that map is the lifetime tally the `insectCaught:<id>` ladders,
 *  `insectSpecies` and the Field Guide's "found" flag all read, so decrementing it un-discovered a species
 *  the moment its last copy was sold. */
export function insectStockOf(caught: Record<string, number>, sold: Record<string, number>): Record<string, number> {
  const out: Record<string, number> = {};
  for (const [id, n] of Object.entries(caught)) {
    const left = n - (sold[id] ?? 0);
    if (left > 0) out[id] = left;
  }
  return out;
}

/** Defensive coerce of the persisted set of insect CONDITIONS the player has revealed (via floating-note
 *  pickups). Entries are `"<insectId>:<condKey>"`; a bare id from an older save is grandfathered to "all
 *  conditions revealed" rather than re-locked — see sim/clueReveal.ts. */
export function coerceRevealedBugs(raw: unknown): string[] {
  return coerceReveals(raw, (id) => BY_ID.has(id));
}
